/**
 * ClientProfileModal Component - Tab-based Client Profile Interface
 *
 * Features:
 * - Clean header with only client name (no description text)
 * - Two tabs: "Тренировки" and "Упражнения"
 * - "Тренировки" tab: Toggle switch for upcoming/past workouts
 * - "Упражнения" tab: Search functionality and exercise details
 * - Profile section with phone, Instagram, and avatar
 * - Action buttons for upcoming workouts: View, Reschedule, Cancel
 * - Unified layout for both upcoming and past workouts with consistent styling
 * - Paginated workout history (10 per page) with detailed card view
 * - Fully responsive design for desktop and mobile
 *
 * Layout Structure:
 * - Header: Client name only
 * - Profile Section: Avatar, phone, Instagram fields
 * - Tabs: "Тренировки" and "Упражнения"
 *   - Workouts Tab: Toggle switch + unified workout cards (upcoming/past)
 *   - Exercises Tab: Search input + exercise list
 * - Footer: Close button
 *
 * Unified Workout Card Layout:
 * - Consistent card design for both upcoming and past workouts
 * - Date and time badges in header row
 * - Workout comments displayed below
 * - Action buttons: View (both), Reschedule/Cancel (upcoming only)
 * - Responsive flex layout adapting to screen size
 *
 * Tab Navigation:
 * - Default active tab: "Тренировки" (shows upcoming workouts)
 * - Active tab highlighting: Green background (bg-green-500)
 * - Smooth transitions between tabs
 *
 * Toggle Switch Functionality (within Workouts tab):
 * - Default state: showUpcoming = true (предстоящие тренировки)
 * - Visual feedback: active label highlighted in primary color
 * - Smooth transitions between states
 * - Maintains pagination state for past workouts
 *
 * Responsive Design:
 * - Desktop (≥1024px): Full layout with side-by-side elements
 * - Mobile (≤480px): Stacked layout with optimized spacing
 * - Touch-friendly buttons and proper spacing
 * - Tabs adapt to screen size with proper grid layout
 * - Unified card layout works consistently across breakpoints
 *
 * Accessibility:
 * - Proper ARIA labels for all interactive elements
 * - Keyboard navigation support for tabs
 * - Screen reader friendly markup
 * - Focus management for toggle switch and tab navigation
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getClientWorkouts, getClientBalance, getClientPayments, getClientExercises, getClientExerciseHistory, getClientExerciseWorkingWeights, updateClientExerciseWorkingWeight, deleteWorkout, transferWorkout, cancelWorkout, updateExerciseSet, deleteExerciseSet, createWorkout, createExercise, createNote, getCustomExercises, createCustomExercise, getAllExercises, deleteClient, updateClient, updateWorkout } from '../services/api';
// import { useLoopDetection, withTimeout, LoopValidators } from '../utils/loopDetection';
import WorkoutDetailModal from './WorkoutDetailModal';
import {
  FullScreenModal,
  FullScreenModalContent,
  FullScreenModalHeader,
  FullScreenModalTitle,
  FullScreenModalBackButton,
  FullScreenModalBody,
} from './ui/fullscreen-modal';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { X, ChevronLeft, ChevronRight, Eye, Phone, Instagram, CheckCircle, MessageSquare, Pencil, Trash2, Calendar, CalendarHeart, Plus, Dumbbell, BarChart3, Ruler, Scale, User, CreditCard, Mail, Activity, ChevronDown, ChevronUp, Loader2, Camera, MapPin } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Combobox } from './ui/combobox';
import PaymentsTab from './PaymentsTab';
import { Dialog, DialogContent, DialogTitle, DialogDescription, VisuallyHidden } from './ui/dialog';
import { computeClientUnpaidWorkoutIds, workoutCalendarDayKey } from '../utils/clientPaymentWallet';
import { isWorkoutDebited } from '../utils/workoutDebit';
import { cn } from '../lib/utils';
import { buildApiUrl } from '../config/api';
import { getApiAccessToken } from '../config/authToken';
import { generateUniqueRandomNickname } from '../utils/uniqueNickname';
import { toast } from 'sonner';
import ExerciseAnalyticsModal from './ExerciseAnalyticsModal';
import AvatarPickerModal from './AvatarPickerModal';
import { useI18n } from '../i18n/context';
import { useLocalizedExerciseOptions } from '../i18n/useLocalizedExerciseOptions';
import { getExerciseDisplayLabel, exerciseMatchesSearch } from '../lib/localizeStandardExercises';
import { getIntlLocaleTag, getDateFnsLocale, getSortLocale } from '../lib/localeRuntime';
import { format, addDays } from 'date-fns';
import { API_GROUP_WORKOUT_COMMENT } from '../i18n/workoutConstants';

// Muscle groups (labels via t(`plan.muscle.${value}`) in component)
const MUSCLE_GROUPS_META = [
  { value: 'chest', color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'back', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'legs', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'shoulders', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  { value: 'arms', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  { value: 'core', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'cardio', color: 'bg-pink-100 text-pink-800 border-pink-300' },
  { value: 'other', color: 'bg-gray-100 text-gray-800 border-gray-300' },
];

async function fetchAllClientPaymentsPages(clientId) {
  const limit = 200;
  let offset = 0;
  const all = [];
  for (;;) {
    const res = await getClientPayments(clientId, { limit, offset });
    const rows = Array.isArray(res?.data) ? res.data : [];
    all.push(...rows);
    if (rows.length < limit) break;
    offset += limit;
  }
  return all;
}

function normalizePaymentDateKey(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw);
  const head = s.includes('T') ? s.split('T')[0] : s.slice(0, 10);
  return head.length >= 10 ? head.slice(0, 10) : head;
}

function buildPaymentCalendarCells(monthAnchor) {
  const y = monthAnchor.getFullYear();
  const m = monthAnchor.getMonth();
  const first = new Date(y, m, 1);
  const last = new Date(y, m + 1, 0);
  const startPad = (first.getDay() + 6) % 7;
  const daysInMonth = last.getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push({ type: 'empty' });
  for (let d = 1; d <= daysInMonth; d++) {
    const dk = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ type: 'day', day: d, dayKey: dk });
  }
  while (cells.length % 7 !== 0) cells.push({ type: 'empty' });
  return cells;
}

// Exercise list will be loaded from API

function buildWorkingWeightsMapFromRows(rows) {
  const map = {};
  const toNum = (v) => {
    if (typeof v === 'number' && isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
      const parsed = parseFloat(v.replace(',', '.'));
      return !isNaN(parsed) && isFinite(parsed) ? parsed : null;
    }
    return null;
  };
  for (const r of Array.isArray(rows) ? rows : []) {
    if (r && r.exercise_name) {
      map[r.exercise_name] = {
        ...r,
        manual_working_weight: toNum(r.manual_working_weight),
        max_history_weight: toNum(r.max_history_weight),
        working_weight: toNum(r.working_weight),
      };
    }
  }
  return map;
}

function getCatalogExerciseName(ex) {
  if (!ex) return '';
  return String(ex.value ?? ex.label ?? ex.name ?? '').trim();
}

function getCatalogExerciseLabel(ex) {
  if (!ex) return '';
  return String(ex.label ?? ex.value ?? ex.name ?? '').trim();
}

const ClientProfileModal = ({
  show,
  onHide,
  client,
  trainerId,
  onWorkoutUpdate,
  onExerciseUpdate,
  initialSection,
  initialOpenAddPayment = false,
  unpaidSessionsHint = null,
}) => {
  const { t, locale } = useI18n();
  const localeTag = getIntlLocaleTag(locale);
  const dfLocale = getDateFnsLocale(locale);
  const sortLocale = getSortLocale(locale);

  const muscleGroupCatalog = useMemo(
    () =>
      MUSCLE_GROUPS_META.map((g) => ({
        ...g,
        label: t(`plan.muscle.${g.value}`),
      })),
    [t]
  );

  const paymentCalWeekdays = useMemo(() => {
    const monday = new Date(2024, 0, 8);
    return Array.from({ length: 7 }, (_, i) => format(addDays(monday, i), 'EEE', { locale: dfLocale }));
  }, [dfLocale]);

  const cancelledRef = useRef(false);

  const [workouts, setWorkouts] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [showWorkoutDetail, setShowWorkoutDetail] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [workoutToDelete, setWorkoutToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('single'); // 'single' or 'series'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [instagram, setInstagram] = useState('');
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState('');
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneText, setPhoneText] = useState('');
  const [isEditingInstagram, setIsEditingInstagram] = useState(false);
  const [instagramText, setInstagramText] = useState('');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailText, setEmailText] = useState('');
  // New profile fields
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [gender, setGender] = useState('');
  const [isEditingWeight, setIsEditingWeight] = useState(false);
  const [weightText, setWeightText] = useState('');
  const [isEditingHeight, setIsEditingHeight] = useState(false);
  const [heightText, setHeightText] = useState('');
  const [isEditingGender, setIsEditingGender] = useState(false);
  const [genderText, setGenderText] = useState('');
  const [isChangingAvatar, setIsChangingAvatar] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [localAvatar, setLocalAvatar] = useState('');
  const [clientStatus, setClientStatus] = useState('active');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteCommentDialogOpen, setDeleteCommentDialogOpen] = useState(false);
  // Exercises tab state
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [exerciseSuggestions, setExerciseSuggestions] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [expandedExercises, setExpandedExercises] = useState(new Set());
  const [exerciseHistoryMap, setExerciseHistoryMap] = useState({});
  const [workingWeightsByName, setWorkingWeightsByName] = useState({});
  const [isSavingWorkingWeight, setIsSavingWorkingWeight] = useState(false);
  const [editingSet, setEditingSet] = useState(null);
  const [editReps, setEditReps] = useState('');
  const [editWeight, setEditWeight] = useState('');
  const [showExerciseAnalytics, setShowExerciseAnalytics] = useState(false);
  const [selectedExerciseForAnalytics, setSelectedExerciseForAnalytics] = useState(null);
  // Load max weights from localStorage on mount and when client changes
  const [exerciseMaxWeights, setExerciseMaxWeights] = useState({});
  const [showUpdateMaxWeightDialog, setShowUpdateMaxWeightDialog] = useState(false);
  const [selectedExerciseForMaxWeight, setSelectedExerciseForMaxWeight] = useState(null);
  const [newMaxWeight, setNewMaxWeight] = useState('');
  const [showAddManualExerciseDialog, setShowAddManualExerciseDialog] = useState(false);
  const [addManualExerciseSearch, setAddManualExerciseSearch] = useState('');
  const [addManualExerciseSelectedName, setAddManualExerciseSelectedName] = useState('');
  const [addManualExerciseWeight, setAddManualExerciseWeight] = useState('');
  const [isSavingManualExercise, setIsSavingManualExercise] = useState(false);
  const [exerciseWeightsLoading, setExerciseWeightsLoading] = useState(false);

  // Load max weights from localStorage when client changes
  useEffect(() => {
    if (client?.id) {
      const stored = localStorage.getItem(`exerciseMaxWeights_${client.id}`);
      if (stored) {
        try {
          setExerciseMaxWeights(JSON.parse(stored));
        } catch (e) {
          console.error('Error parsing stored max weights:', e);
          setExerciseMaxWeights({});
        }
      } else {
        setExerciseMaxWeights({});
      }
    } else {
      setExerciseMaxWeights({});
    }
  }, [client?.id]);

  // Save max weights to localStorage whenever they change
  useEffect(() => {
    if (client?.id && Object.keys(exerciseMaxWeights).length > 0) {
      localStorage.setItem(`exerciseMaxWeights_${client.id}`, JSON.stringify(exerciseMaxWeights));
    }
  }, [exerciseMaxWeights, client?.id]);

  // Workouts view toggle state - default shows upcoming (off state)
  const [showUpcoming, setShowUpcoming] = useState(true);

  const [showPaymentCalendar, setShowPaymentCalendar] = useState(false);
  const [paymentCalendarMonth, setPaymentCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [paymentCalendarBalance, setPaymentCalendarBalance] = useState(0);
  const [paymentCalendarPayments, setPaymentCalendarPayments] = useState([]);
  const [paymentCalendarLoading, setPaymentCalendarLoading] = useState(false);

  // Active section state - default to "calendar" (workouts)
  const [activeSection, setActiveSection] = useState(initialSection || 'calendar');
  const modalBodyRef = useRef(null);

  // When opening the modal or switching client, allow caller to force starting section (e.g. payments)
  useEffect(() => {
    if (!show) return;
    if (!initialSection) return;
    setActiveSection(initialSection);
  }, [show, client?.id, initialSection]);

  // Reset to default view when calendar section is selected
  useEffect(() => {
    if (activeSection === 'calendar') {
      setShowUpcoming(true);
      setCurrentPage(1);
    }
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = 0;
    }
  }, [activeSection]);

  // Add workout state
  const [showAddWorkout, setShowAddWorkout] = useState(false);
  
  // Delete client state
  const [showDeleteClientDialog, setShowDeleteClientDialog] = useState(false);
  const [deleteClientMode, setDeleteClientMode] = useState('deactivate'); // 'deactivate' | 'full'
  const [isDeletingClient, setIsDeletingClient] = useState(false);
  
  // Edit profile modal state
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [editProfileData, setEditProfileData] = useState({
    name: '',
    comment: '',
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [localName, setLocalName] = useState('');
  const [localNickname, setLocalNickname] = useState('');
  const fileInputRef = React.useRef(null);
  const [addWorkoutForm, setAddWorkoutForm] = useState({
    date: new Date().toLocaleDateString('en-CA'), // Уже использует локальную дату
    time: '10:00',
    end_time: '11:00',
    comment: '',
    recurrence_type: 'none',
    recurrence_days: []
  });

  // Add exercise to workout state
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [selectedWorkoutForExercise, setSelectedWorkoutForExercise] = useState(null);
  const [addExerciseForm, setAddExerciseForm] = useState({
    name: '',
    sets: [{ reps: '', weight: '' }],
    exerciseName: ''
  });

  // All exercises state (standard + custom)
  const [allExercises, setAllExercises] = useState([]);
  const localizedAllExercises = useLocalizedExerciseOptions(allExercises);
  const getExerciseLabel = useCallback(
    (name) => getExerciseDisplayLabel(name, localizedAllExercises),
    [localizedAllExercises]
  );
  const [customExercises, setCustomExercises] = useState([]);

  // Exercise confirmation dialog state
  const [showExerciseConfirmDialog, setShowExerciseConfirmDialog] = useState(false);
  const [pendingExerciseName, setPendingExerciseName] = useState('');
  const [pendingExerciseAction, setPendingExerciseAction] = useState(null);

  // Workout exercises state
  const [workoutExercises, setWorkoutExercises] = useState({});
  const [loadingWorkoutExercises, setLoadingWorkoutExercises] = useState({});

  const getEffectiveTrainerId = () => {
    try {
      const savedUser = localStorage.getItem('currentUser');
      if (savedUser) {
        const user = JSON.parse(savedUser);
        if (user?.id) return user.id;
      }
    } catch (e) {
      console.error('Error getting trainer_id from localStorage:', e);
    }
    return 'default-trainer';
  };

  // Function to load all exercises (standard + custom)
  const loadAllExercises = async () => {
    try {
      const response = await getAllExercises(getEffectiveTrainerId());
      setAllExercises(response.data);
    } catch (error) {
      console.error('Error loading all exercises:', error);
    }
  };

  // Keep local display fields in sync with selected client
  useEffect(() => {
    setLocalName(client?.name || '');
    setLocalNickname(client?.nickname || '');
  }, [client?.id]);

  const dispatchClientUpdated = (patch) => {
    if (!client?.id) return;
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent('mad:clientUpdated', {
        detail: { id: client.id, ...patch },
      })
    );
  };

  // Function to fetch exercises for a specific workout
  const fetchWorkoutExercises = async (workoutId) => {
    if (workoutExercises[workoutId] || loadingWorkoutExercises[workoutId]) {
      return; // Already loaded or loading
    }

    setLoadingWorkoutExercises(prev => ({ ...prev, [workoutId]: true }));

    try {
      const response = await fetch(`/api/exercises?workout_id=${workoutId}`);
      const exercises = await response.json();
      
      setWorkoutExercises(prev => ({ 
        ...prev, 
        [workoutId]: exercises.map(ex => ex.name) 
      }));
    } catch (error) {
      console.error('Error loading workout exercises:', error);
      setWorkoutExercises(prev => ({ 
        ...prev, 
        [workoutId]: [] 
      }));
    } finally {
      setLoadingWorkoutExercises(prev => ({ ...prev, [workoutId]: false }));
    }
  };

  // Function to save new exercise to trainer's dictionary
  const saveNewExercise = async (exerciseName) => {
    try {
      await createCustomExercise({
        name: exerciseName.trim(),
        trainer_id: getEffectiveTrainerId()
      });

      // Update local state to include the new exercise
      setCustomExercises(prev => [...prev, {
        id: Date.now(), // Temporary ID until refresh
        name: exerciseName.trim(),
        trainer_id: getEffectiveTrainerId()
      }]);

      // Refresh all exercises to sync across components
      await loadAllExercises();

      // Trigger refresh in parent components
      if (onExerciseUpdate) onExerciseUpdate();

      setSuccessMessage(t('clientProfile.dictExerciseAdded', { name: exerciseName.trim() }));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving new exercise:', error);
      if (error.response?.status === 409) {
        setErrorMessage(t('clientProfile.errDictExerciseExists'));
      } else {
        setErrorMessage(t('clientProfile.errSaveExerciseToDict'));
      }
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Function to handle exercise confirmation
  const handleExerciseConfirmation = async (exerciseName, action) => {
    setPendingExerciseName(exerciseName);
    setPendingExerciseAction(() => action);
    setShowExerciseConfirmDialog(true);
  };

  // Function to confirm saving exercise to dictionary
  const confirmSaveExercise = async () => {
    if (pendingExerciseAction) {
      await saveNewExercise(pendingExerciseName);
      await pendingExerciseAction();
    }
    setShowExerciseConfirmDialog(false);
    setPendingExerciseName('');
    setPendingExerciseAction(null);
  };

  // Function to cancel exercise save
  const cancelSaveExercise = async () => {
    if (pendingExerciseAction) {
      await pendingExerciseAction();
    }
    setShowExerciseConfirmDialog(false);
    setPendingExerciseName('');
    setPendingExerciseAction(null);
  };

  // Edit workout state
  const [showEditWorkout, setShowEditWorkout] = useState(false);
  const [editWorkoutForm, setEditWorkoutForm] = useState({
    date: '',
    time: '',
    end_time: '',
    comment: ''
  });

  const daysOfWeek = useMemo(() => {
    const monday = new Date(2024, 0, 8);
    return [1, 2, 3, 4, 5, 6, 0].map((value) => {
      const offset = value === 0 ? 6 : value - 1;
      const d = addDays(monday, offset);
      return { value, label: format(d, 'EEE', { locale: dfLocale }) };
    });
  }, [dfLocale]);

  // Constants
  const WORKOUTS_PER_PAGE = 10;
  // Use current date instead of hardcoded date
  const currentDate = new Date();
  const today = currentDate.toLocaleDateString('en-CA'); // Уже использует локальную дату

  // Helper function to create date-time object for comparison
  const createDateTime = (date, time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day, hours, minutes, 0, 0);
  };

  const getWorkoutEndTime = (workoutDate, startTime, endTime) => {
    const start = createDateTime(workoutDate, startTime);
    if (!endTime) {
      return new Date(start.getTime() + 60 * 60 * 1000);
    }
    const end = createDateTime(workoutDate, endTime);
    if (end <= start) {
      end.setDate(end.getDate() + 1);
    }
    return end;
  };

  // Function to check if workout is completed by time
  const isWorkoutCompletedByTime = (workout) => {
    if (!workout || !workout.date || !workout.time) return false;
    
    if (workout.status === 'cancelled' || workout.status === 'transferred') {
      return false;
    }
    
    if (workout.status === 'completed') {
      return true;
    }
    
    try {
      const now = new Date();
      const workoutEndTime = getWorkoutEndTime(workout.date, workout.time, workout.end_time);
      return now > workoutEndTime;
    } catch (error) {
      console.error('Error checking workout completion:', error);
      return false;
    }
  };

  // Filter and sort workouts based on current time
  const currentTime = currentDate;
  const upcomingWorkouts = workouts
    .filter(w => {
      if (!w.date || !w.time) return false;
      const workoutEndTime = getWorkoutEndTime(w.date, w.time, w.end_time);
      const isUpcoming = workoutEndTime > currentTime && w.status !== 'cancelled' && w.status !== 'transferred';
      return isUpcoming;
    })
    .sort((a, b) => {
      const dateTimeA = createDateTime(a.date, a.time);
      const dateTimeB = createDateTime(b.date, b.time);
      return dateTimeA - dateTimeB; // Sort from earliest to latest
    });

  const pastWorkouts = workouts
    .filter(w => {
      if (!w.date || !w.time) return false;
      const workoutEndTime = getWorkoutEndTime(w.date, w.time, w.end_time);
      const isPast = workoutEndTime < currentTime && w.status !== 'cancelled' && w.status !== 'transferred';
      return isPast;
    })
    .sort((a, b) => {
      const dateTimeA = createDateTime(a.date, a.time);
      const dateTimeB = createDateTime(b.date, b.time);
      return dateTimeB - dateTimeA; // Sort from latest to earliest
    });

  // Pagination for both past and upcoming workouts
  const pastWorkoutsTotalPages = Math.ceil(pastWorkouts.length / WORKOUTS_PER_PAGE);
  const upcomingWorkoutsTotalPages = Math.ceil(upcomingWorkouts.length / WORKOUTS_PER_PAGE);
  
  // Current page validation
  const validCurrentPage = Math.min(currentPage, Math.max(1, showUpcoming ? upcomingWorkoutsTotalPages : pastWorkoutsTotalPages));
  
  // Pagination for current view
  const startIndex = (validCurrentPage - 1) * WORKOUTS_PER_PAGE;
  const endIndex = startIndex + WORKOUTS_PER_PAGE;
  
  const currentPageWorkouts = showUpcoming 
    ? upcomingWorkouts.slice(startIndex, endIndex)
    : pastWorkouts.slice(startIndex, endIndex);
  
  const totalPages = showUpcoming ? upcomingWorkoutsTotalPages : pastWorkoutsTotalPages;

  useEffect(() => {
    if (!show) {
      setShowWorkoutDetail(false);
      setSelectedWorkout(null);
    }
  }, [show]);

  useEffect(() => {
    cancelledRef.current = false;
    if (show && client) {
      setShowUpcoming(true);
      setCurrentPage(1);
      loadClientData();
      loadCustomExercises();
      // Load client profile data from database
      setPhoneNumber(client.phone || '');
      setInstagram(client.instagram || '');
      setEmail(client.email || '');
      setComment(client.comment || '');
      setCommentText(client.comment || '');
      setPhoneText(client.phone || '');
      setInstagramText(client.instagram || '');
      setEmailText(client.email || '');
      setWeight(client.weight ? String(client.weight) : '');
      setHeight(client.height ? String(client.height) : '');
      setGender(client.gender || '');
      setClientStatus(client.status || 'active');
      setLocalAvatar(client.avatar || '');
      setWeightText(client.weight ? String(client.weight) : '');
      setHeightText(client.height ? String(client.height) : '');
      setGenderText(client.gender || '');
      setIsEditingPhone(false);
      setIsEditingInstagram(false);
      setIsEditingEmail(false);
      setIsEditingWeight(false);
      setIsEditingHeight(false);
      setIsEditingGender(false);
      // Reset add workout form
      resetAddWorkoutForm();
    }
    return () => { cancelledRef.current = true; };
  }, [show, client]);

  const loadClientData = async () => {
    if (!client) return;

    setLoading(true);
    try {
      const workoutsResponse = await getClientWorkouts(client.id);
      if (cancelledRef.current) return;
      setWorkouts(workoutsResponse.data);

      try {
        const exercisesResponse = await getClientExercises(client.id);
        if (cancelledRef.current) return;
        setExercises(exercisesResponse.data);
      } catch (exercisesError) {
        if (cancelledRef.current) return;
        console.warn('Error loading exercises (this may be normal if client has no exercises):', exercisesError);
        setExercises([]);
      }

      await loadAllExercises();
      if (cancelledRef.current) return;

      preloadPaymentDataForOffline(client.id).catch(() => {});
    } catch (error) {
      if (cancelledRef.current) return;
      console.error('Error loading client data:', error);
      setErrorMessage(t('clientProfile.errLoadClient'));
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  };

  // Preload balance + history so they're cached for offline
  const preloadPaymentDataForOffline = async (clientId) => {
    if (!clientId) return;
    try {
      const preOpts = { credentials: 'include' };
      const preBearer = getApiAccessToken();
      if (preBearer) preOpts.headers = { Authorization: `Bearer ${preBearer}` };
      await fetch(buildApiUrl(`/clients/${clientId}/balance`), preOpts);
      await fetch(buildApiUrl(`/clients/${clientId}/history?limit=10&offset=0`), preOpts);
      await fetch(buildApiUrl(`/clients/${clientId}/history?limit=10&offset=0&type=payment`), preOpts);
    } catch {
      // ignore - preload is best-effort
    }
  };

  useEffect(() => {
    setShowPaymentCalendar(false);
  }, [client?.id]);

  const paymentCalendarUnpaidIds = useMemo(
    () => computeClientUnpaidWorkoutIds(workouts, paymentCalendarBalance),
    [workouts, paymentCalendarBalance]
  );

  const paymentQuantityByDayKey = useMemo(() => {
    const m = new Map();
    for (const p of paymentCalendarPayments) {
      const key = normalizePaymentDateKey(p.payment_date);
      if (!key) continue;
      const q = Number(p.quantity);
      m.set(key, (m.get(key) || 0) + (Number.isFinite(q) ? q : 0));
    }
    return m;
  }, [paymentCalendarPayments]);

  const paymentCalendarCells = useMemo(
    () => buildPaymentCalendarCells(paymentCalendarMonth),
    [paymentCalendarMonth]
  );

  const openPaymentCalendar = useCallback(async () => {
    if (!client?.id) return;
    const d = new Date();
    setPaymentCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    setShowPaymentCalendar(true);
    setPaymentCalendarLoading(true);
    try {
      const [balRes, payRows, woRes] = await Promise.all([
        getClientBalance(client.id),
        fetchAllClientPaymentsPages(client.id),
        getClientWorkouts(client.id),
      ]);
      const b = balRes?.data?.balance;
      setPaymentCalendarBalance(typeof b === 'number' && Number.isFinite(b) ? b : 0);
      setPaymentCalendarPayments(payRows);
      if (Array.isArray(woRes?.data)) setWorkouts(woRes.data);
    } catch (e) {
      console.error(e);
      toast.error(t('clientProfile.errPaymentCalLoad'));
    } finally {
      setPaymentCalendarLoading(false);
    }
  }, [client?.id, t]);

  const loadCustomExercises = async () => {
    try {
      const trainerId = getEffectiveTrainerId();
      // Load all exercises (standard + custom) through unified API
      const allExercisesResponse = await getAllExercises(trainerId);
      setAllExercises(allExercisesResponse.data);

      // Also load just custom exercises for backward compatibility
      const customResponse = await getCustomExercises(trainerId);
      setCustomExercises(customResponse.data);
    } catch (error) {
      console.warn('Error loading exercises:', error);
      setAllExercises([]);
      setCustomExercises([]);
    }
  };

  /** Список из истории тренировок + упражнения, добавленные только с рабочим весом (ручная запись) */
  const displayExercises = useMemo(() => {
    const fromApi = Array.isArray(exercises) ? exercises : [];
    const byName = new Map(fromApi.map((e) => [e.name, { ...e }]));
    for (const name of Object.keys(workingWeightsByName || {})) {
      if (name && !byName.has(name)) {
        byName.set(name, {
          name,
          workout_count: 0,
          total_sets: 0,
          max_weight: null,
          avg_weight: null,
          max_reps: null,
          avg_reps: null,
          last_workout_date: null,
        });
      }
    }
    return Array.from(byName.values()).sort((a, b) =>
      getExerciseDisplayLabel(a.name, localizedAllExercises).localeCompare(
        getExerciseDisplayLabel(b.name, localizedAllExercises),
        sortLocale
      )
    );
  }, [exercises, workingWeightsByName, sortLocale, localizedAllExercises]);

  const refreshClientExerciseWeights = useCallback(async () => {
    if (!client?.id) return;
    const resp = await getClientExerciseWorkingWeights(client.id);
    const rows = Array.isArray(resp?.data) ? resp.data : [];
    setWorkingWeightsByName(buildWorkingWeightsMapFromRows(rows));
  }, [client?.id]);

  // Load working weights (все имена: история + только ручной вес) при открытии вкладки «Упражнения»
  useEffect(() => {
    if (activeSection !== 'exercises') {
      setExerciseWeightsLoading(false);
      return;
    }
    if (!client?.id) return;

    let cancelled = false;
    setExerciseWeightsLoading(true);
    (async () => {
      try {
        const resp = await getClientExerciseWorkingWeights(client.id);
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        if (!cancelled) setWorkingWeightsByName(buildWorkingWeightsMapFromRows(rows));
      } catch (e) {
        console.error('[ClientProfileModal] Failed to load working weights:', e);
        if (!cancelled) setWorkingWeightsByName({});
      } finally {
        if (!cancelled) setExerciseWeightsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeSection, client?.id, exercises]);

  // Preload exercise history for offline when exercises tab is active
  useEffect(() => {
    if (activeSection !== 'exercises') return;
    if (!client?.id) return;
    const toPreload = displayExercises.map((e) => e?.name).filter(Boolean);
    if (toPreload.length === 0) return;
    toPreload.forEach((name) => {
      getClientExerciseHistory(client.id, name).catch(() => {});
    });
  }, [activeSection, client?.id, displayExercises]);

  // Action handlers

  const handleCancelWorkout = async (workoutId) => {
    try {
      setErrorMessage(''); // Clear any previous errors
      const response = await cancelWorkout(workoutId);
      setSuccessMessage(t('clientProfile.successWorkoutCancelled'));
      await loadClientData(); // Refresh data and wait for it
      // Notify parent component to refresh main workouts view
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error cancelling workout:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      let errorMessage = t('clientProfile.errCancelWorkout');
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = t('clientProfile.errWithMessage', { msg: error.message });
      }
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleRescheduleWorkout = async (workoutId) => {
    if (!rescheduleDate || !rescheduleTime) {
      setErrorMessage(t('workout.alert.fillDateTime'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      setErrorMessage(''); // Clear any previous errors
      const response = await transferWorkout(workoutId, {
        newDate: rescheduleDate,
        newTime: rescheduleTime
      });
      setSuccessMessage(t('clientProfile.successWorkoutMoved'));
      setShowRescheduleDialog(false);
      setRescheduleDate('');
      setRescheduleTime('');
      await loadClientData(); // Refresh data and wait for it
      // Notify parent component to refresh main workouts view
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error rescheduling workout:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      let errorMessage = t('clientProfile.errTransferWorkout');
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        if (error.response.status === 409 && error.response.data.conflictingClients) {
          errorMessage = t('workout.alert.timeBusy', {
            clients: error.response.data.conflictingClients.join(', '),
          });
        } else if (error.response.status === 400 && error.response.data.type === 'same_client_conflict') {
          errorMessage = t('workout.alert.sameClientConflict');
        }
      } else if (error.message) {
        errorMessage = t('clientProfile.errWithMessage', { msg: error.message });
      }
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const openRescheduleDialog = (workout) => {
    setSelectedWorkout(workout);
    setRescheduleDate(workout.date);
    setRescheduleTime(workout.time);
    setShowRescheduleDialog(true);
  };

  const viewWorkoutDetail = (workout) => {
    if (!workout?.id) return;
    setSelectedWorkout({
      ...workout,
      client_id: workout.client_id ?? client?.id,
      client_name: workout.client_name ?? client?.name,
      client_nickname: workout.client_nickname ?? client?.nickname,
    });
    setShowWorkoutDetail(true);
  };

  // Utility functions
  const formatDate = (dateString) => {
    // Parse date components explicitly to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(localeTag, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateWithoutYear = (dateString) => {
    // Format date without year for the new display format
    // Parse date components explicitly to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(localeTag, {
      day: 'numeric',
      month: 'short'
    });
  };

  const formatTime = (timeString) => {
    return timeString.substring(0, 5); // HH:MM format
  };

  // Enhanced formatting for workout display
  const formatWorkoutDateTime = (dateString, timeString, endTimeString) => {
    // Parse date components explicitly to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const formattedDate = date.toLocaleDateString(localeTag, {
      day: 'numeric',
      month: 'short'
    });
    const time = formatTime(timeString);
    const endTime = endTimeString ? formatTime(endTimeString) : null;
    
    return {
      date: formattedDate,
      time: time,
      endTime: endTime,
      fullTimeRange: endTime ? `${time} - ${endTime}` : time
    };
  };

  /** Комментарий к тренировке для списка (пусто = не показывать) */
  const getWorkoutCommentForList = (w) => {
    const c = w?.comment;
    if (c == null || typeof c !== 'string') return '';
    const trimmedComment = c.trim();
    if (!trimmedComment || trimmedComment === API_GROUP_WORKOUT_COMMENT) return '';
    return trimmedComment;
  };

  // Comment management functions
   const handleSaveComment = async () => {
     try {
       setErrorMessage(''); // Clear any previous errors
       const trimmedComment = commentText.trim();
       await updateClient(client.id, {
         nickname: client.nickname, // Required by backend
         comment: trimmedComment || null
       });
       setComment(trimmedComment || null);
       setIsEditingComment(false);
       setSuccessMessage(
         trimmedComment ? t('workout.success.commentSaved') : t('workout.success.commentRemoved')
       );
       setTimeout(() => setSuccessMessage(''), 3000);

       // Notify parent component to refresh client list
       if (onWorkoutUpdate) {
         onWorkoutUpdate();
       }
     } catch (error) {
       console.error('Error saving comment:', error);
       setErrorMessage(t('workout.err.commentSave'));
       setTimeout(() => setErrorMessage(''), 3000);
     }
   };

  const handleEditComment = () => {
    setCommentText(comment);
    setIsEditingComment(true);
  };

  const handleDeleteComment = async () => {
    try {
      setErrorMessage(''); // Clear any previous errors

      const result = await updateClient(client.id, {
        nickname: client.nickname, // Required by backend
        comment: null
      });

      setComment('');
      setCommentText('');
      setIsEditingComment(false);
      setSuccessMessage(t('workout.success.commentRemoved'));
      setTimeout(() => setSuccessMessage(''), 3000);

      dispatchClientUpdated({ comment: null });

      // Notify parent component to refresh client list
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      console.error('Error details:', error.response?.data || error.message);

      // More specific error handling
      if (error.response?.status === 404) {
        setErrorMessage(t('clientProfile.errCommentDeleteNotFound'));
      } else if (error.response?.status === 500) {
        setErrorMessage(t('clientProfile.errCommentDeleteServer'));
      } else {
        setErrorMessage(t('clientProfile.errCommentDelete'));
      }
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleCancelEditComment = () => {
    setCommentText(comment);
    setIsEditingComment(false);
  };

  // Phone number management functions
  const handleSavePhone = async () => {
    try {
      setErrorMessage('');
      const trimmedPhone = phoneText.trim();
      await updateClient(client.id, {
        nickname: client.nickname,
        phone: trimmedPhone || null
      });
      setPhoneNumber(trimmedPhone || null);
      setIsEditingPhone(false);
      setSuccessMessage(
        trimmedPhone ? t('clientProfile.phoneSaved') : t('clientProfile.phoneCleared')
      );
      setTimeout(() => setSuccessMessage(''), 3000);

      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error saving phone:', error);
      setErrorMessage(t('clientProfile.errPhone'));
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleEditPhone = () => {
    setPhoneText(phoneNumber || '');
    setIsEditingPhone(true);
  };

  const handleCancelEditPhone = () => {
    setPhoneText(phoneNumber || '');
    setIsEditingPhone(false);
  };

  // Instagram management functions
  const handleSaveInstagram = async () => {
    try {
      setErrorMessage('');
      const trimmedInstagram = instagramText.trim();
      await updateClient(client.id, {
        nickname: client.nickname,
        instagram: trimmedInstagram || null
      });
      setInstagram(trimmedInstagram || null);
      setIsEditingInstagram(false);
      setSuccessMessage(
        trimmedInstagram ? t('clientProfile.instagramSaved') : t('clientProfile.instagramCleared')
      );
      setTimeout(() => setSuccessMessage(''), 3000);

      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error saving instagram:', error);
      setErrorMessage(t('clientProfile.errInstagram'));
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleEditInstagram = () => {
    setInstagramText(instagram || '');
    setIsEditingInstagram(true);
  };

  const handleCancelEditInstagram = () => {
    setInstagramText(instagram || '');
    setIsEditingInstagram(false);
  };

  // Email management functions
  const handleSaveEmail = async () => {
    try {
      setErrorMessage('');
      const trimmedEmail = emailText.trim();
      await updateClient(client.id, {
        nickname: client.nickname,
        email: trimmedEmail || null
      });
      setEmail(trimmedEmail || null);
      setIsEditingEmail(false);
      setSuccessMessage(trimmedEmail ? t('clientProfile.emailSaved') : t('clientProfile.emailCleared'));
      setTimeout(() => setSuccessMessage(''), 3000);

      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error saving email:', error);
      setErrorMessage(t('clientProfile.errEmail'));
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleEditEmail = () => {
    setEmailText(email || '');
    setIsEditingEmail(true);
  };

  const handleCancelEditEmail = () => {
    setEmailText(email || '');
    setIsEditingEmail(false);
  };

  // Weight management functions
  const handleSaveWeight = async () => {
    try {
      setErrorMessage('');
      const trimmedWeight = weightText.trim();
      const weightValue = trimmedWeight ? parseFloat(trimmedWeight) : null;
      await updateClient(client.id, {
        nickname: client.nickname,
        weight: weightValue
      });
      setWeight(trimmedWeight || '');
      setIsEditingWeight(false);
      setSuccessMessage(trimmedWeight ? t('clientProfile.weightSaved') : t('clientProfile.weightCleared'));
      setTimeout(() => setSuccessMessage(''), 3000);

      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error saving weight:', error);
      setErrorMessage(t('clientProfile.errWeight'));
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleEditWeight = () => {
    setWeightText(weight || '');
    setIsEditingWeight(true);
  };

  const handleCancelEditWeight = () => {
    setWeightText(weight || '');
    setIsEditingWeight(false);
  };

  // Height management functions
  const handleSaveHeight = async () => {
    try {
      setErrorMessage('');
      const trimmedHeight = heightText.trim();
      const heightValue = trimmedHeight ? parseFloat(trimmedHeight) : null;
      await updateClient(client.id, {
        nickname: client.nickname,
        height: heightValue
      });
      setHeight(trimmedHeight || '');
      setIsEditingHeight(false);
      setSuccessMessage(
        trimmedHeight ? t('clientProfile.heightSaved') : t('clientProfile.heightCleared')
      );
      setTimeout(() => setSuccessMessage(''), 3000);

      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error saving height:', error);
      setErrorMessage(t('clientProfile.errHeight'));
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleEditHeight = () => {
    setHeightText(height || '');
    setIsEditingHeight(true);
  };

  const handleCancelEditHeight = () => {
    setHeightText(height || '');
    setIsEditingHeight(false);
  };

  // Gender management functions
  const handleSaveGender = async () => {
    try {
      setErrorMessage('');
      const trimmedGender = genderText.trim();
      await updateClient(client.id, {
        nickname: client.nickname,
        gender: trimmedGender || null
      });
      setGender(trimmedGender || '');
      setIsEditingGender(false);
      setSuccessMessage(
        trimmedGender ? t('clientProfile.genderSaved') : t('clientProfile.genderCleared')
      );
      setTimeout(() => setSuccessMessage(''), 3000);

      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error saving gender:', error);
      setErrorMessage(t('clientProfile.errGender'));
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleEditGender = () => {
    setGenderText(gender || '');
    setIsEditingGender(true);
  };

  const handleCancelEditGender = () => {
    setGenderText(gender || '');
    setIsEditingGender(false);
  };

  // Function to change avatar - open picker (plus optional upload)
  const handleChangeAvatar = async () => {
    setShowAvatarPicker(true);
  };

  const handleSelectAvatarFromLibrary = async (avatarUrl) => {
    if (!client?.id) return;
    setIsChangingAvatar(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      await updateClient(client.id, {
        avatar: avatarUrl,
      });
      setLocalAvatar(avatarUrl || '');
      dispatchClientUpdated({ avatar: avatarUrl || '' });
      setSuccessMessage(t('clientProfile.avatarUpdated'));
      setTimeout(() => setSuccessMessage(''), 3000);
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error selecting avatar:', error);
      setErrorMessage(error.response?.data?.error || t('clientProfile.errAvatarUpdate'));
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setIsChangingAvatar(false);
    }
  };

  const handleAvatarFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrorMessage(t('clientProfile.errPickImage'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage(t('clientProfile.errFileSize5mb'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

      setIsChangingAvatar(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64String = reader.result;
      
      await updateClient(client.id, {
            avatar: base64String
      });
          setLocalAvatar(base64String || '');
          dispatchClientUpdated({ avatar: base64String || '' });
      
          setSuccessMessage(t('clientProfile.avatarUploaded'));
          setTimeout(() => setSuccessMessage(''), 3000);
          
          // Refresh parent to get updated client data
      if (onWorkoutUpdate) {
            onWorkoutUpdate();
          }
        } catch (error) {
          console.error('Error uploading avatar:', error);
          setErrorMessage(error.response?.data?.error || t('clientProfile.errAvatarUpload'));
          setTimeout(() => setErrorMessage(''), 5000);
        } finally {
          setIsChangingAvatar(false);
        }
      };
      reader.onerror = () => {
        setErrorMessage(t('clientProfile.errFileRead'));
        setIsChangingAvatar(false);
        setTimeout(() => setErrorMessage(''), 5000);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error handling avatar change:', error);
      setErrorMessage(t('clientProfile.errFileProcess'));
      setIsChangingAvatar(false);
      setTimeout(() => setErrorMessage(''), 5000);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Calculate BMI
  const calculateBMI = () => {
    if (!weight || !height || parseFloat(weight) === 0 || parseFloat(height) === 0) {
      return null;
    }
    const weightKg = parseFloat(weight);
    const heightM = parseFloat(height) / 100; // Convert cm to m
    if (heightM === 0) return null;
    return (weightKg / (heightM * heightM)).toFixed(1);
  };


  // Handle saving profile
  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const safe = (v) => (v == null ? '' : String(v)).trim();

      const existingNick = (client?.nickname || '').toString().trim().toLowerCase();
      const nickOk = /^[a-z0-9._-]{3,32}$/.test(existingNick);
      let nicknameToSave = nickOk ? existingNick : null;
      if (!nicknameToSave) {
        try {
          nicknameToSave = await generateUniqueRandomNickname();
        } catch (e) {
          console.error(e);
          setErrorMessage(e?.message || t('clientProfile.errNickname'));
          setIsSavingProfile(false);
          return;
        }
      }

      const nextName = safe(editProfileData.name) || '';
      const nextComment = safe(editProfileData.comment);

      await updateClient(client.id, {
        nickname: nicknameToSave,
        name: nextName || null,
        comment: nextComment || null,
      });

      setLocalName(nextName);
      setLocalNickname(nicknameToSave);
      setComment(nextComment || '');

      setSuccessMessage(t('clientProfile.profileUpdated'));
      setShowEditProfileModal(false);
      setTimeout(() => setSuccessMessage(''), 3000);
      dispatchClientUpdated({
        name: nextName,
        nickname: nicknameToSave,
        comment: nextComment || null,
      });

      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setErrorMessage(error.response?.data?.error || t('clientProfile.errProfileSave'));
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Function to get muscle groups from workout exercises
  const getWorkoutMuscleGroups = async (workoutId) => {
    try {
      // Load exercises if not already loaded
      if (!workoutExercises[workoutId]) {
        await fetchWorkoutExercises(workoutId);
      }

      const exerciseNames = workoutExercises[workoutId] || [];
      const muscleGroupsSet = new Set();

      // Get muscle groups from all exercises
      for (const exerciseName of exerciseNames) {
        const exercise = allExercises.find(ex => ex.name === exerciseName);
        if (exercise && exercise.muscle_group) {
          muscleGroupsSet.add(exercise.muscle_group);
        }
      }

      return Array.from(muscleGroupsSet);
    } catch (error) {
      console.error('Error getting muscle groups:', error);
      return [];
    }
  };

  // Exercise-related functions
  const handleExerciseSearch = (value) => {
    setExerciseSearch(value);
    if (value.trim()) {
      const q = value.toLowerCase();
      const suggestions = displayExercises
        .filter((exercise) => exerciseMatchesSearch(exercise.name, q, localizedAllExercises))
        .map((exercise) => getExerciseLabel(exercise.name))
        .slice(0, 5);
      setExerciseSuggestions(suggestions);
    } else {
      setExerciseSuggestions([]);
    }
  };

  const handleExerciseSelect = (exerciseName) => {
    const exercise = displayExercises.find((ex) => ex.name === exerciseName);
    if (exercise) {
      toggleExerciseDetail(exercise);
    }
    setExerciseSearch('');
    setExerciseSuggestions([]);
  };

  const toggleExerciseDetail = async (exercise) => {
    const exerciseKey = exercise.name;
    const isExpanded = expandedExercises.has(exerciseKey);

    if (isExpanded) {
      // Collapse
      const newExpanded = new Set(expandedExercises);
      newExpanded.delete(exerciseKey);
      setExpandedExercises(newExpanded);
    } else {
      // Expand - load history if not already loaded
      const newExpanded = new Set(expandedExercises);
      newExpanded.add(exerciseKey);
      setExpandedExercises(newExpanded);
    setSelectedExercise(exercise);

      // Load history if not already loaded
      if (!exerciseHistoryMap[exerciseKey]) {
    try {
      const historyResponse = await getClientExerciseHistory(client.id, exercise.name);
          setExerciseHistoryMap(prev => ({
            ...prev,
            [exerciseKey]: historyResponse.data
          }));
    } catch (error) {
      console.error('Error loading exercise history:', error);
      setErrorMessage(t('workout.err.historyLoad'));
        }
      }
    }
  };

  const handleUpdateExerciseSet = async (setId, reps, weight) => {
    try {
      await updateExerciseSet(setId, { reps: parseInt(reps), weight: weight ? parseFloat(weight) : null });
      setSuccessMessage(t('clientProfile.successSetUpdated'));
      // Refresh exercise history
      if (selectedExercise) {
        const exerciseKey = selectedExercise.name;
        const historyResponse = await getClientExerciseHistory(client.id, selectedExercise.name);
        setExerciseHistoryMap(prev => ({
          ...prev,
          [exerciseKey]: historyResponse.data
        }));
      }
      setEditingSet(null);
      setEditReps('');
      setEditWeight('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating exercise set:', error);
      setErrorMessage(t('clientProfile.errSetUpdate'));
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleDeleteExerciseSet = async (setId) => {
    try {
      await deleteExerciseSet(setId);
      setSuccessMessage(t('clientProfile.successSetDeleted'));
      // Refresh exercise history
      if (selectedExercise) {
        const exerciseKey = selectedExercise.name;
        const historyResponse = await getClientExerciseHistory(client.id, selectedExercise.name);
        setExerciseHistoryMap(prev => ({
          ...prev,
          [exerciseKey]: historyResponse.data
        }));
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting exercise set:', error);
      setErrorMessage(t('clientProfile.errSetDelete'));
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const startEditingSet = (set) => {
    setEditingSet(set.id);
    setEditReps(set.reps.toString());
    setEditWeight(set.weight ? set.weight.toString() : '');
  };

  const cancelEditingSet = () => {
    setEditingSet(null);
    setEditReps('');
    setEditWeight('');
  };

  // Функция для автоматического расчета времени окончания
  const calculateEndTime = (startTime) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const endHours = (hours + 1) % 24; // Добавляем 1 час
    return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleTimeChange = (newTime) => {
    setAddWorkoutForm({
      ...addWorkoutForm,
      time: newTime,
      end_time: calculateEndTime(newTime)
    });
  };

  const handleDayToggle = (dayValue) => {
    setAddWorkoutForm(prev => ({
      ...prev,
      recurrence_days: prev.recurrence_days.includes(dayValue)
        ? prev.recurrence_days.filter(d => d !== dayValue)
        : [...prev.recurrence_days, dayValue]
    }));
  };

  // Add workout functions
  const handleAddWorkout = async () => {
    if (!addWorkoutForm.date || !addWorkoutForm.time) {
      setErrorMessage(t('addWorkout.errDateTime'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      const workoutData = {
        client_id: client.id,
        date: addWorkoutForm.date,
        time: addWorkoutForm.time,
        end_time: addWorkoutForm.end_time,
        comment: addWorkoutForm.comment,
        recurrence_type: addWorkoutForm.recurrence_type,
        recurrence_days: addWorkoutForm.recurrence_days
      };

      const resp = await createWorkout(workoutData);
      if (resp?.status === 202 && resp?.data?.queued) {
        toast.success(t('app.offlineSaved'));
        return;
      }

      setSuccessMessage(t('clientProfile.successWorkoutAdded'));
      setShowAddWorkout(false);
      resetAddWorkoutForm();

      // Refresh data
      await loadClientData();
      // Notify parent component to refresh main workouts view
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error adding workout:', error);

      if (error.response && error.response.data) {
        const errorData = error.response.data;

        if (errorData.type === 'same_client_conflict') {
          setErrorMessage(t('addWorkout.errAlreadyCreated'));
        } else if (
          errorData.type === 'time_conflict' &&
          errorData.date &&
          errorData.time &&
          Array.isArray(errorData.conflictingClients)
        ) {
          setErrorMessage(
            t('clientProfile.errRecurringConflict', {
              date: errorData.date,
              time: errorData.time,
              clients: errorData.conflictingClients.join(', '),
            })
          );
        } else if (errorData.type === 'time_conflict' && Array.isArray(errorData.conflictingClients)) {
          setErrorMessage(
            t('clientProfile.errTimeConflictShort', {
              clients: errorData.conflictingClients.join(', '),
            })
          );
        } else {
          setErrorMessage(errorData.error || t('addWorkout.errCreate'));
        }
      } else {
        setErrorMessage(t('clientProfile.errWorkoutAdd'));
      }
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const resetAddWorkoutForm = () => {
    setAddWorkoutForm({
      date: new Date().toLocaleDateString('en-CA'),
      time: '10:00',
      end_time: '11:00',
      comment: '',
      recurrence_type: 'none',
      recurrence_days: []
    });
  };

  // Handler for adding exercise to existing workout
  const handleAddExerciseToWorkout = (workout) => {
    setSelectedWorkoutForExercise(workout);
    setAddExerciseForm({
      name: '',
      sets: '',
      reps: '',
      weight: ''
    });
    setShowAddExercise(true);
  };


  // Function to show confirmation dialog for new exercise
  const promptSaveNewExercise = (exerciseName) => {
    if (!exerciseName.trim()) return;
    setPendingExerciseName(exerciseName.trim());
    setPendingExerciseAction(null);
    setShowExerciseConfirmDialog(true);
  };

  // Function to save new exercise to trainer's dictionary (called after confirmation)
  const confirmSaveNewExercise = async () => {
    if (!pendingExerciseName.trim()) return;

    try {
      setErrorMessage(''); // Clear any previous errors
      setShowExerciseConfirmDialog(false);

      const response = await createCustomExercise({
        name: pendingExerciseName.trim(),
        trainer_id: getEffectiveTrainerId()
      });

      setSuccessMessage(t('clientProfile.dictExerciseAdded', { name: pendingExerciseName }));
      setTimeout(() => setSuccessMessage(''), 3000);

      // Update the local custom exercises list
      const newCustomExercise = {
        id: response.data.id,
        name: pendingExerciseName.trim(),
        trainer_id: getEffectiveTrainerId(),
        created_at: response.data.created_at
      };

      setCustomExercises(prev => [...prev, newCustomExercise]);

      // Update all exercises list with the new custom exercise
      const newExercise = {
        value: pendingExerciseName.trim(),
        label: pendingExerciseName.trim(),
        type: 'custom',
        id: response.data.id
      };
      setAllExercises(prev => [...prev, newExercise]);

      // Update the form with the newly added exercise
      setAddExerciseForm({...addExerciseForm, name: pendingExerciseName.trim()});
      setPendingExerciseName('');

      // Notify parent component to refresh exercises
      if (onExerciseUpdate) {
        onExerciseUpdate();
      }
    } catch (error) {
      console.error('Error saving new exercise:', error);
      if (error.response?.status === 409) {
        setErrorMessage(t('clientProfile.errDictExerciseExists'));
      } else {
        setErrorMessage(error.response?.data?.error || t('clientProfile.errSaveExerciseToDict'));
      }
      setTimeout(() => setErrorMessage(''), 3000);
      setPendingExerciseName('');
    }
  };

  // Function to cancel saving new exercise
  const cancelSaveNewExercise = () => {
    setShowExerciseConfirmDialog(false);
    setPendingExerciseName('');
    setPendingExerciseAction(null);
  };

  // Handler for adding exercise to workout
  const handleAddExercise = async () => {
    if (!addExerciseForm.name || addExerciseForm.sets.some(set => !set.reps)) {
      setErrorMessage(t('addWorkout.errRequired'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    // Validate sets data (только целые числа)
    if (addExerciseForm.sets.length > 0) {
      for (let i = 0; i < addExerciseForm.sets.length; i++) {
        const set = addExerciseForm.sets[i];
        const setNumber = i + 1;

        // Check if reps is missing or invalid (только целые числа)
        if (!set.reps || set.reps === '' || !/^\d+$/.test(set.reps) || parseInt(set.reps) <= 0) {
          setErrorMessage(t('clientProfile.errSetRepsInvalid', { n: setNumber }));
          setTimeout(() => setErrorMessage(''), 3000);
          return;
        }

        // Check if weight is invalid (десятичные числа, если указан)
        if (set.weight !== '' && (!/^\d+([.,]\d+)?$/.test(set.weight) || parseFloat(set.weight.replace(',', '.')) < 0)) {
          setErrorMessage(t('clientProfile.errSetWeightInvalid', { n: setNumber }));
          setTimeout(() => setErrorMessage(''), 3000);
          return;
        }
      }
    }

    if (!selectedWorkoutForExercise) return;

    // Check if exercise is new and offer to save it
    const isNewExercise = !allExercises.some(exercise =>
      exercise.value.toLowerCase() === addExerciseForm.name.trim().toLowerCase()
    );

    if (isNewExercise) {
      // Store the exercise addition action to execute after confirmation
      const addExerciseAction = async () => {
        try {
          setErrorMessage(''); // Clear any previous errors

          const exerciseData = {
            workout_id: selectedWorkoutForExercise.id,
            name: addExerciseForm.name,
            sets: addExerciseForm.sets.map((set, index) => ({
              set_number: index + 1,
              reps: parseInt(set.reps),
              weight: set.weight ? parseFloat(set.weight) : null
            }))
          };

          await createExercise(exerciseData);

          // Reset form and close modal
          setAddExerciseForm({ name: '', sets: [{ reps: '', weight: '' }] });
          setShowAddExercise(false);

          // Refresh data
          await loadClientData();

          setSuccessMessage(t('workout.success.exerciseAdded'));
          setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
          console.error('Error adding exercise:', error);
          setErrorMessage(t('workout.err.exerciseAdd'));
          setTimeout(() => setErrorMessage(''), 3000);
        }
      };

      await handleExerciseConfirmation(addExerciseForm.name.trim(), addExerciseAction);
      return; // Exit early, the action will be executed after confirmation
    }

    try {
      setErrorMessage(''); // Clear any previous errors

      const exerciseData = {
        workout_id: selectedWorkoutForExercise.id,
        name: addExerciseForm.name,
        sets: addExerciseForm.sets.map((set, index) => ({
          set_number: index + 1,
          reps: parseInt(set.reps),
          weight: set.weight ? parseFloat(set.weight) : null
        }))
      };

      await createExercise(exerciseData);
      setSuccessMessage(t('workout.success.exerciseAdded'));
      setShowAddExercise(false);
      resetAddExerciseForm();
      await loadClientData(); // Refresh data and wait for it
      // Notify parent component to refresh main workouts view
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error adding exercise:', error);
      setErrorMessage(t('workout.err.exerciseAdd'));
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Handler for editing workout
  const handleEditWorkout = (workout) => {
    setSelectedWorkout(workout);
    setEditWorkoutForm({
      date: workout.date,
      time: workout.time,
      end_time: workout.end_time || '',
      comment: workout.comment || ''
    });
    setShowEditWorkout(true);
  };

  // Handler for saving edited workout
  const handleSaveEditedWorkout = async () => {
    if (!editWorkoutForm.date || !editWorkoutForm.time) {
      setErrorMessage(t('workout.alert.fillDateTime'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      setErrorMessage(''); // Clear any previous errors
      await updateWorkout(selectedWorkout.id, {
        date: editWorkoutForm.date,
        time: editWorkoutForm.time,
        end_time: editWorkoutForm.end_time,
        comment: editWorkoutForm.comment
      });
      setSuccessMessage(t('clientProfile.successWorkoutUpdated'));
      setShowEditWorkout(false);
      await loadClientData(); // Refresh data and wait for it
      // Notify parent component to refresh main workouts view
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating workout:', error);
      setErrorMessage(t('clientProfile.errWorkoutUpdate'));
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Handler for deleting workout
  // Check if workout is part of a recurring series
  const isPartOfSeries = (workout) => {
    if (!workout) return false;

    // Find workouts with same client, time, and day of week (excluding cancelled)
    const similarWorkouts = workouts.filter(w =>
      w.client_id === workout.client_id &&
      w.time === workout.time &&
      new Date(w.date).getDay() === new Date(workout.date).getDay() &&
      w.status !== 'cancelled' &&
      w.id !== workout.id
    );

    return similarWorkouts.length > 0;
  };

  const handleDeleteWorkout = async (workoutId, deleteType = 'single') => {
    try {
      setErrorMessage(''); // Clear any previous errors

      if (deleteType === 'single') {
        await deleteWorkout(workoutId);
        setSuccessMessage(t('clientProfile.successWorkoutDeleted'));
      } else if (deleteType === 'series') {
        // Find and delete all workouts in the series
        const workoutToDelete = workouts.find(w => w.id === workoutId);
        if (!workoutToDelete) {
          throw new Error(t('clientProfile.errWorkoutNotFound'));
        }

        // Find all workouts with same client, time, and day of week
        const seriesWorkouts = workouts.filter(w =>
          w.client_id === workoutToDelete.client_id &&
          w.time === workoutToDelete.time &&
          new Date(w.date).getDay() === new Date(workoutToDelete.date).getDay() &&
          w.status !== 'cancelled'
        );

        // Delete each workout in series
        for (const seriesWorkout of seriesWorkouts) {
          try {
            await deleteWorkout(seriesWorkout.id);
          } catch (deleteError) {
            console.warn(`Failed to delete workout ${seriesWorkout.id}:`, deleteError);
          }
        }

        setSuccessMessage(t('clientProfile.successSeriesDeleted', { n: seriesWorkouts.length }));
      }

      await loadClientData(); // Refresh data and wait for it
      // Notify parent component to refresh main workouts view
      if (onWorkoutUpdate) {
        onWorkoutUpdate();
      }
      setTimeout(() => setSuccessMessage(''), 3000);
      setWorkoutToDelete(null);
      setDeleteType('single');
    } catch (error) {
      console.error('Error deleting workout:', error);
      setErrorMessage(t('clientProfile.errWorkoutDelete'));
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const openDeleteDialog = (workout) => {
    setWorkoutToDelete(workout);
    setDeleteType('single');
  };

  // Functions for handling sets in add exercise form
  const handleAddSet = () => {
    setAddExerciseForm({
      ...addExerciseForm,
      sets: [...addExerciseForm.sets, { reps: '', weight: '' }]
    });
  };

  const handleRemoveSet = (index) => {
    if (addExerciseForm.sets.length > 1) {
      setAddExerciseForm({
        ...addExerciseForm,
        sets: addExerciseForm.sets.filter((_, i) => i !== index)
      });
    }
  };

  const handleSetChange = (index, field, value) => {
    // Валидация для повторений (только целые числа) и веса (десятичные числа)
    if (field === 'reps') {
      // Разрешаем только целые числа для повторений
      if (value === '' || /^\d+$/.test(value)) {
        const newSets = [...addExerciseForm.sets];
        newSets[index][field] = value;
        setAddExerciseForm({
          ...addExerciseForm,
          sets: newSets
        });
      }
    } else if (field === 'weight') {
      // Разрешаем десятичные числа для веса (запятая или точка)
      if (value === '' || /^\d+([.,]\d+)?$/.test(value)) {
        const newSets = [...addExerciseForm.sets];
        newSets[index][field] = value;
        setAddExerciseForm({
          ...addExerciseForm,
          sets: newSets
        });
      }
    } else {
      // Для других полей сохраняем текущую логику
      const newSets = [...addExerciseForm.sets];
      newSets[index][field] = value;
      setAddExerciseForm({
        ...addExerciseForm,
        sets: newSets
      });
    }
  };

  // Reset add exercise form
  const resetAddExerciseForm = () => {
    setAddExerciseForm({
      name: '',
      sets: [{ reps: '', weight: '' }],
      exerciseName: ''
    });
  };

  // Функция для обновления упражнений тренировки (для обновления шильдиков)
  const refreshWorkoutExercises = async (workoutId) => {
    if (!workoutId) return;
    // Clear cached exercises for this workout to force refresh
    setWorkoutExercises(prev => {
      const updated = { ...prev };
      delete updated[workoutId];
      return updated;
    });
    // Reload exercises for this workout
    await fetchWorkoutExercises(workoutId);
  };

  // Функции для WorkoutDetailModal
  const handleAddExerciseToWorkoutDetail = async (workoutId, exerciseData) => {
    try {
      await createExercise({
        workout_id: workoutId,
        ...exerciseData
      });
      // Refresh exercises for this workout to update muscle group badges
      await refreshWorkoutExercises(workoutId);
      loadClientData(); // Refresh client data
      if (onWorkoutUpdate) {
        onWorkoutUpdate(); // Notify parent component
      }
    } catch (error) {
      console.error('Error adding exercise:', error);
      setErrorMessage(t('workout.err.exerciseAdd'));
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  // Handler for exercise updates (delete, edit) in WorkoutDetailModal
  const handleExerciseUpdateInWorkout = async () => {
    if (selectedWorkout?.id) {
      // Refresh exercises for the current workout to update muscle group badges
      await refreshWorkoutExercises(selectedWorkout.id);
    }
    loadClientData(); // Refresh client data
    if (onWorkoutUpdate) {
      onWorkoutUpdate(); // Notify parent component
    }
  };

  const handleAddNote = async (workoutId, noteData) => {
    try {
      await createNote({
        workout_id: workoutId,
        ...noteData
      });
      loadClientData(); // Refresh client data
      if (onWorkoutUpdate) {
        onWorkoutUpdate(); // Notify parent component
      }
    } catch (error) {
      console.error('Error adding note:', error);
      setErrorMessage(t('clientProfile.errNoteAdd'));
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleDeleteClient = async () => {
    if (!client?.id) return;
    
    setIsDeletingClient(true);
    setErrorMessage('');
    
    try {
      const response = await deleteClient(client.id, deleteClientMode);
      
      if (deleteClientMode === 'full') {
        toast.success(t('clientProfile.toastClientDeleted'));
      } else {
        const deletedCount = response.data?.deletedFutureWorkouts || 0;
        toast.success(
          deletedCount > 0
            ? t('clientProfile.toastClientDeactivatedN', { n: deletedCount })
            : t('clientProfile.toastClientDeactivated')
        );
      }
      
      setShowDeleteClientDialog(false);
      setDeleteClientMode('deactivate');
      try {
        window.dispatchEvent(new CustomEvent('mad:refetchUserContext'));
      } catch {
        /* ignore */
      }
      onHide();
      if (onWorkoutUpdate) onWorkoutUpdate();
      
    } catch (error) {
      console.error('Error deleting/deactivating client:', error);
      setErrorMessage(
        deleteClientMode === 'full'
          ? t('clientProfile.errDeleteClient')
          : t('clientProfile.errDeactivateClient')
      );
    } finally {
      setIsDeletingClient(false);
    }
  };


  return (
    <>
      <FullScreenModal open={show} onOpenChange={onHide}>
        <FullScreenModalContent className="flex flex-col">
          {/* Header with Navigation */}
          <FullScreenModalHeader>
            <FullScreenModalBackButton onClick={onHide} />
            <FullScreenModalTitle className="text-lg font-bold">
              {t('clientProfile.title')}
            </FullScreenModalTitle>
            {!showEditProfileModal && (
              <Button
                onClick={() => {
                  setEditProfileData({
                    name: client?.name || '',
                    comment: comment || '',
                  });
                  setShowEditProfileModal(true);
                }}
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 flex items-center justify-center rounded border-primary/30 hover:bg-primary/5 -mr-2"
                title={t('clientProfile.edit')}
              >
                <Pencil className="h-4 w-4 text-primary" />
              </Button>
            )}
            {showEditProfileModal && <div className="w-10" />}
          </FullScreenModalHeader>

          {/* Body Content */}
          <FullScreenModalBody ref={modalBodyRef}>

          {/* Success/Error Messages */}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          )}
          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
              <X className="h-4 w-4 text-red-600" />
              <p className="text-sm text-red-800">{errorMessage}</p>
            </div>
          )}

          {/* Profile Layout: Centered Vertical Design */}
          {!showEditProfileModal && (() => {
            const handleEditClick = (e) => {
              e?.stopPropagation?.();
              setEditProfileData({
                name: localName || client?.name || '',
                comment: comment || '',
              });
              setShowEditProfileModal(true);
            };

            const commentTrimmed = comment && String(comment).trim();

            return (
              <div className="mb-6 flex flex-col items-center">
                {/* Avatar - Centered */}
                <div className="relative mb-4">
                  <Avatar 
                    className="h-24 w-24 sm:h-28 sm:w-28 cursor-pointer hover:opacity-80 transition-opacity touch-manipulation"
                    onClick={handleChangeAvatar}
                    title={t('clientProfile.changePhoto')}
                  >
                  <AvatarImage src={localAvatar || client?.avatar} alt={localName || localNickname} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-2xl sm:text-3xl">
                    {(localName || localNickname)?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {isChangingAvatar && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                      <Loader2 className="h-6 w-6 sm:h-7 sm:w-7 animate-spin text-white" />
                    </div>
                  )}
                  {/* Edit Button Overlay - Camera Icon on bottom of avatar */}
                  <button
                    onClick={handleChangeAvatar}
                    className="absolute bottom-0 right-0 w-8 h-8 sm:w-9 sm:h-9 bg-primary rounded-full flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors border-2 border-background"
                    title={t('clientProfile.changePhoto')}
                  >
                    <Camera className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                  </button>
                  {/* Activity Indicator */}
                  <div 
                    className={`absolute top-0 right-0 w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 border-background ${
                      clientStatus === 'active' ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                    title={
                      clientStatus === 'active'
                        ? t('clientProfile.statusActive')
                        : t('clientProfile.statusInactive')
                    }
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarFileChange}
                    className="hidden"
                  />
                </div>

                {/* Name - Centered below avatar */}
                <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6">
                  {localName || localNickname || t('clientProfile.displayNameFallback')}
                  {typeof unpaidSessionsHint === 'number' && unpaidSessionsHint > 0 ? (
                    <span className="font-normal text-muted-foreground"> ({unpaidSessionsHint})</span>
                  ) : null}
                </h2>

                {commentTrimmed ? (
                  <Card className="w-full max-w-2xl border border-border shadow-sm">
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center">
                          <MessageSquare className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-base text-foreground">
                              {t('clientProfile.trainerComment')}
                            </span>
                            <div className="flex items-center gap-0.5 shrink-0">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9"
                                title={t('clientProfile.editTrainerComment')}
                                onClick={handleEditClick}
                              >
                                <Pencil className="h-4 w-4 text-muted-foreground" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-destructive hover:text-destructive"
                                title={t('clientProfile.deleteTrainerComment')}
                                onClick={() => setDeleteCommentDialogOpen(true)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-sm sm:text-[15px] leading-relaxed text-foreground/90 whitespace-pre-wrap break-words pr-1">
                            {comment}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : null}

                <AlertDialog open={deleteCommentDialogOpen} onOpenChange={setDeleteCommentDialogOpen}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('clientProfile.deleteTrainerCommentTitle')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('clientProfile.deleteTrainerCommentDesc')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={async (e) => {
                          e.preventDefault();
                          setDeleteCommentDialogOpen(false);
                          await handleDeleteComment();
                        }}
                      >
                        {t('common.delete')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            );
          })()}

          {/* Key Metrics Section - Height, Weight, BMI - At the bottom */}
          {height && weight && parseFloat(height) > 0 && parseFloat(weight) > 0 && (
            <div className="mb-4 flex gap-3 sm:gap-4 md:gap-6">
              <div className="flex flex-col items-center flex-1">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <span className="text-lg sm:text-xl font-bold text-primary">{height}</span>
                </div>
                <span className="text-xs text-muted-foreground">{t('clientProfile.metricsHeight')}</span>
              </div>
              <div className="flex flex-col items-center flex-1">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <span className="text-lg sm:text-xl font-bold text-primary">{weight}</span>
                </div>
                <span className="text-xs text-muted-foreground">{t('clientProfile.metricsWeight')}</span>
              </div>
              {calculateBMI() && (
                <div className="flex flex-col items-center flex-1">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                    <span className="text-lg sm:text-xl font-bold text-primary">{calculateBMI()}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">BMI</span>
                </div>
              )}
            </div>
          )}

          {/* Unified Profile Section - Edit mode */}
          {showEditProfileModal && (
            <div className="mb-4 sm:mb-6">
              <Card className="border">
                <CardContent className="p-4">
                  <div className="pt-4 space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base font-semibold">{t('clientProfile.editProfileTitle')}</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowEditProfileModal(false);
                          // Reset to original values
                          setEditProfileData({
                            name: client?.name || '',
                            comment: comment || '',
                          });
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-profile-name">{t('clientProfile.nameLabel')}</Label>
                        <Input
                          id="edit-profile-name"
                          type="text"
                          value={editProfileData.name}
                          onChange={(e) => setEditProfileData({ ...editProfileData, name: e.target.value })}
                          placeholder={t('clientProfile.namePlaceholder')}
                          maxLength={50}
                          className="text-sm placeholder:text-slate-400"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="edit-comment">{t('clientProfile.clientNoteLabel')}</Label>
                        <Textarea
                          id="edit-comment"
                          value={editProfileData.comment}
                          onChange={(e) => setEditProfileData({ ...editProfileData, comment: e.target.value })}
                          placeholder={t('clientProfile.clientNotePlaceholder')}
                          className="min-h-[100px] text-sm placeholder:text-slate-400"
                          rows={4}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 pt-2">
                        <Button
                    onClick={handleSaveProfile}
                    disabled={isSavingProfile}
                    className="flex-1"
                  >
                    {isSavingProfile ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('clientProfile.savingProfile')}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {t('clientProfile.saveProfileChanges')}
                      </>
                    )}
                        </Button>
                        <Button
                          variant="outline"
                      onClick={() => {
                        setShowEditProfileModal(false);
                        setEditProfileData({
                          name: client?.name || '',
                          comment: comment || '',
                        });
                      }}
                    disabled={isSavingProfile}
                    className="flex-1 sm:flex-initial"
                        >
                          {t('common.cancel')}
                        </Button>
                      </div>
                    </div>
                </CardContent>
              </Card>
            </div>
          )}


          {/* Widget Buttons Navigation - 3 buttons in one row: Календарь, Упражнения, Оплаты */}
          <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-4">
            <Button
              onClick={() => setActiveSection('calendar')}
              variant={activeSection === 'calendar' ? 'default' : 'outline'}
              className={`h-16 sm:h-24 flex flex-col items-center justify-center gap-1.5 sm:gap-2 ${
                activeSection === 'calendar' ? 'bg-green-500 text-white hover:bg-green-600' : ''
              }`}
            >
              <Calendar className="h-5 w-5 sm:h-7 sm:w-7" />
              <span className="text-[11px] sm:text-sm font-medium max-[420px]:hidden">
                {t('clientProfile.navCalendar')}
              </span>
            </Button>
            <Button
              onClick={() => setActiveSection('exercises')}
              variant={activeSection === 'exercises' ? 'default' : 'outline'}
              className={`h-16 sm:h-24 flex flex-col items-center justify-center gap-1.5 sm:gap-2 ${
                activeSection === 'exercises' ? 'bg-green-500 text-white hover:bg-green-600' : ''
              }`}
            >
              <Dumbbell className="h-5 w-5 sm:h-7 sm:w-7" />
              <span className="text-[11px] sm:text-sm font-medium max-[420px]:hidden">
                {t('clientProfile.navExercises')}
              </span>
            </Button>
            <Button
              onClick={() => setActiveSection('payments')}
              variant={activeSection === 'payments' ? 'default' : 'outline'}
              className={`h-16 sm:h-24 flex flex-col items-center justify-center gap-1.5 sm:gap-2 ${
                activeSection === 'payments' ? 'bg-green-500 text-white hover:bg-green-600' : ''
              }`}
            >
              <CreditCard className="h-5 w-5 sm:h-7 sm:w-7" />
              <span className="text-[11px] sm:text-sm font-medium max-[420px]:hidden">
                {t('clientProfile.navPayments')}
              </span>
            </Button>
          </div>

          {/* Content Sections */}
          {activeSection === 'calendar' && (
            <div className="mt-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">{t('clientProfile.workoutsHeading')}</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0 rounded-lg"
                    onClick={openPaymentCalendar}
                    aria-label={t('clientProfile.paymentCalendarAndSessions')}
                    title={t('clientProfile.openPaymentCalendar')}
                  >
                    <CalendarHeart className="h-5 w-5" />
                  </Button>
                </div>

                {/* Segmented Control - Предстоящие / Прошедшие */}
                <div className="inline-flex rounded-lg border border-input bg-muted p-1">
                  <button
                    onClick={() => setShowUpcoming(true)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      showUpcoming
                        ? 'bg-green-500 text-white'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('clientProfile.upcoming')}
                  </button>
                  <button
                    onClick={() => setShowUpcoming(false)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      !showUpcoming
                        ? 'bg-green-500 text-white'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('clientProfile.past')}
                  </button>
                </div>
              </div>

            {/* Workouts Content */}
            {showUpcoming ? (
              /* Предстоящие тренировки */
              <>
                {loading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <span className="ml-2">{t('clientProfile.loading')}</span>
                  </div>
                ) : currentPageWorkouts.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{t('clientProfile.noUpcomingWorkouts')}</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      {(() => {
                        const thisYear = new Date().getFullYear();
                        let lastRenderedYear = null;
                        return currentPageWorkouts.map(workout => {
                        const workoutYear = workout.date ? new Date(workout.date).getFullYear() : null;
                        const showYearHeader = workoutYear && workoutYear !== thisYear && workoutYear !== lastRenderedYear;
                        if (workoutYear && workoutYear !== thisYear) {
                          lastRenderedYear = workoutYear;
                        }

                        // Determine workout status
                        const isCompleted = isWorkoutCompletedByTime(workout) || workout.status === 'completed';
                        const isCancelled = workout.status === 'cancelled';
                        const isTransferred = workout.status === 'transferred';
                        const workoutDateTime = formatWorkoutDateTime(workout.date, workout.time, workout.end_time);
                        const listComment = getWorkoutCommentForList(workout);
                        
                        // Determine status for badge
                        let status = 'scheduled';
                        if (isCompleted) {
                          status = 'completed';
                        } else if (workout.status === 'skipped') {
                          status = 'skipped';
                        } else if (isCancelled) {
                          status = 'cancelled';
                        } else if (isTransferred) {
                          status = 'transferred';
                        }

                        const getStatusText = () => {
                          const key = `payments.status.${status}`;
                          const localized = t(key);
                          return localized !== key ? localized : t('payments.status.workout');
                        };

                        const getBadgeVariant = () => {
                          switch (status) {
                            case 'in_progress': return 'default';
                            case 'scheduled': return 'default';
                            case 'completed': return 'default';
                            case 'past_day': return 'default';
                                case 'skipped': return 'destructive';
                            case 'cancelled': return 'destructive';
                            case 'transferred': return 'destructive';
                            default: return 'default';
                          }
                        };

                        // Fetch exercises for this workout if not already loaded
                        if (!workoutExercises[workout.id] && !loadingWorkoutExercises[workout.id]) {
                          fetchWorkoutExercises(workout.id);
                        }

                        const exerciseNames = workoutExercises[workout.id] || [];
                        const isLoadingExercises = loadingWorkoutExercises[workout.id];
                        
                        // Get muscle groups from exercises
                        const planMuscleBadges = [];
                        if (exerciseNames.length > 0 && allExercises.length > 0) {
                          exerciseNames.forEach(exName => {
                            const exercise = allExercises.find(ex => ex.value === exName || ex.label === exName);
                            if (exercise && exercise.muscle_group) {
                              const muscleGroup = muscleGroupCatalog.find(mg => mg.value === exercise.muscle_group);
                              if (muscleGroup && !planMuscleBadges.find(mg => mg.value === muscleGroup.value)) {
                                planMuscleBadges.push(muscleGroup);
                              }
                            }
                          });
                        }

                        return (
                          <React.Fragment key={workout.id}>
                            {showYearHeader && (
                              <div className="flex items-center gap-3 pt-2 pb-1">
                                <div className="h-px flex-1 bg-border" />
                                <span className="text-sm font-semibold text-muted-foreground">{workoutYear}</span>
                                <div className="h-px flex-1 bg-border" />
                              </div>
                            )}
                            <Card
                              className={`cursor-pointer transition-colors hover:bg-accent/50 ${isCompleted ? 'opacity-75' : isCancelled ? 'opacity-75' : ''}`}
                              onClick={() => viewWorkoutDetail(workout)}
                            >
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div
                                      className={`min-w-0 w-full truncate text-sm leading-snug ${isCancelled ? 'line-through text-muted-foreground' : ''}`}
                                      title={`${workoutDateTime.date} · ${workoutDateTime.fullTimeRange}`}
                                    >
                                      <span className="font-semibold tabular-nums">{workoutDateTime.date}</span>
                                      <span className="text-muted-foreground mx-1.5">·</span>
                                      <span className="tabular-nums">{workoutDateTime.fullTimeRange}</span>
                                    </div>
                                    {listComment ? (
                                      <p className="text-xs text-muted-foreground mt-1.5 whitespace-pre-wrap break-words line-clamp-4">
                                        {listComment}
                                      </p>
                                    ) : null}
                          {/* Exercises list */}
                          {isLoadingExercises ? (
                            <div className="text-xs text-muted-foreground mt-2">{t('clientProfile.loadingExercises')}</div>
                          ) : exerciseNames.length > 0 && (
                            <div className="text-xs text-muted-foreground mt-2">
                              {exerciseNames.join(', ')}
                            </div>
                          )}
                                    {/* Muscle Group Badges */}
                                    {planMuscleBadges.length > 0 && (
                                      <div className="flex flex-wrap gap-1.5 mt-2">
                                        {planMuscleBadges.map((mg) => (
                                          <Badge
                                            key={mg.value}
                                            variant="outline"
                                            className={`text-xs ${mg.color} rounded-full px-2 py-0.5`}
                                          >
                                            {mg.label}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-shrink-0">
                                    <Badge 
                                      variant={getBadgeVariant()}
                                      className={
                                        status === 'completed' || status === 'past_day'
                                          ? 'bg-gray-500 text-white border-transparent hover:bg-gray-600'
                                          : status === 'in_progress'
                                          ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200'
                                          : ''
                                      }
                                    >
                                      <Dumbbell className="mr-1 h-3 w-3" />
                                      {getStatusText()}
                                    </Badge>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </React.Fragment>
                        );
                      });
                      })()}
                    </div>

                    {/* Pagination for upcoming workouts */}
                    {totalPages > 1 && upcomingWorkouts.length > 0 && (
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="text-sm text-muted-foreground">
                          {t('clientProfile.paginationShown', {
                            from: upcomingWorkouts.length > 0 ? startIndex + 1 : 0,
                            to: Math.min(endIndex, upcomingWorkouts.length),
                            total: upcomingWorkouts.length,
                          })}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={validCurrentPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            {t('clientProfile.back')}
                          </Button>
                          <span className="text-sm">
                            {t('clientProfile.pageOf', { page: validCurrentPage, pages: totalPages })}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={validCurrentPage === totalPages}
                          >
                            {t('clientProfile.next')}
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              /* История тренировок */
              <>
                {loading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <span className="ml-2">{t('clientProfile.loading')}</span>
                  </div>
                ) : currentPageWorkouts.length === 0 ? (
                  <div className="text-center py-8">
                    <Eye className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{t('clientProfile.noPastWorkouts')}</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      {(() => {
                        const thisYear = new Date().getFullYear();
                        let lastRenderedYear = null;
                        return currentPageWorkouts.map(workout => {
                        const workoutYear = workout.date ? new Date(workout.date).getFullYear() : null;
                        const showYearHeader = workoutYear && workoutYear !== thisYear && workoutYear !== lastRenderedYear;
                        if (workoutYear && workoutYear !== thisYear) {
                          lastRenderedYear = workoutYear;
                        }

                        // Determine workout status
                        const isCompleted = isWorkoutCompletedByTime(workout) || workout.status === 'completed';
                        const isCancelled = workout.status === 'cancelled';
                        const isTransferred = workout.status === 'transferred';
                        const workoutDateTime = formatWorkoutDateTime(workout.date, workout.time, workout.end_time);
                        const listComment = getWorkoutCommentForList(workout);
                        
                        // For past workouts, they are always completed (unless cancelled/transferred/skipped)
                        let status = isCompleted ? 'completed' : 'past_day';
                        if (workout.status === 'skipped') {
                          status = 'skipped';
                        } else if (isCancelled) {
                          status = 'cancelled';
                        } else if (isTransferred) {
                          status = 'transferred';
                        }

                        const getStatusText = () => {
                          const key = `payments.status.${status}`;
                          const localized = t(key);
                          return localized !== key ? localized : t('payments.status.workout');
                        };

                        const getBadgeVariant = () => {
                          switch (status) {
                            case 'in_progress': return 'default';
                            case 'scheduled': return 'default';
                            case 'completed': return 'default';
                            case 'past_day': return 'default';
                            case 'skipped': return 'destructive';
                            case 'cancelled': return 'destructive';
                            case 'transferred': return 'destructive';
                            default: return 'default';
                          }
                        };

                        // Fetch exercises for this workout if not already loaded
                        if (!workoutExercises[workout.id] && !loadingWorkoutExercises[workout.id]) {
                          fetchWorkoutExercises(workout.id);
                        }

                        const exerciseNames = workoutExercises[workout.id] || [];
                        const isLoadingExercises = loadingWorkoutExercises[workout.id];
                        
                        // Get muscle groups from exercises
                        const pastPlanMuscleBadges = [];
                        if (exerciseNames.length > 0 && allExercises.length > 0) {
                          exerciseNames.forEach(exName => {
                            const exercise = allExercises.find(ex => ex.value === exName || ex.label === exName);
                            if (exercise && exercise.muscle_group) {
                              const muscleGroup = muscleGroupCatalog.find(mg => mg.value === exercise.muscle_group);
                              if (muscleGroup && !pastPlanMuscleBadges.find(mg => mg.value === muscleGroup.value)) {
                                pastPlanMuscleBadges.push(muscleGroup);
                              }
                            }
                          });
                        }

                        return (
                          <React.Fragment key={workout.id}>
                            {showYearHeader && (
                              <div className="flex items-center gap-3 pt-2 pb-1">
                                <div className="h-px flex-1 bg-border" />
                                <span className="text-sm font-semibold text-muted-foreground">{workoutYear}</span>
                                <div className="h-px flex-1 bg-border" />
                              </div>
                            )}
                            <Card
                              className={`cursor-pointer transition-colors hover:bg-accent/50 ${isCompleted ? 'opacity-75' : isCancelled ? 'opacity-75' : ''}`}
                              onClick={() => viewWorkoutDetail(workout)}
                            >
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div
                                      className={`min-w-0 w-full truncate text-sm leading-snug ${isCancelled ? 'line-through text-muted-foreground' : ''}`}
                                      title={
                                        workoutDateTime.endTime
                                          ? `${workoutDateTime.date} · ${workoutDateTime.time}–${workoutDateTime.endTime}`
                  : `${workoutDateTime.date} · ${workoutDateTime.time}`
                                      }
                                    >
                                      <span className="font-semibold tabular-nums">{workoutDateTime.date}</span>
                                      <span className="text-muted-foreground mx-1.5">·</span>
                                      <span className="tabular-nums">{workoutDateTime.time}</span>
                                    </div>
                                    {listComment ? (
                                      <p className="text-xs text-muted-foreground mt-1.5 whitespace-pre-wrap break-words line-clamp-4">
                                        {listComment}
                                      </p>
                                    ) : null}
                          {isLoadingExercises ? (
                            <div className="text-xs text-muted-foreground mt-2">{t('clientProfile.loadingExercises')}</div>
                          ) : exerciseNames.length > 0 ? (
                            <div className="text-xs text-muted-foreground mt-2">
                              {exerciseNames.join(', ')}
                            </div>
                          ) : null}
                                    {/* Muscle Group Badges */}
                                    {pastPlanMuscleBadges.length > 0 && (
                                      <div className="flex flex-wrap gap-1.5 mt-2">
                                        {pastPlanMuscleBadges.map((mg) => (
                                          <Badge
                                            key={mg.value}
                                            variant="outline"
                                            className={`text-xs ${mg.color} rounded-full px-2 py-0.5`}
                                          >
                                            {mg.label}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge 
                                      variant={getBadgeVariant()}
                                      className={
                                        status === 'completed' || status === 'past_day'
                                          ? 'bg-gray-500 text-white border-transparent hover:bg-gray-600'
                                          : status === 'in_progress'
                                          ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200'
                                          : ''
                                      }
                                    >
                                      <Dumbbell className="mr-1 h-3 w-3" />
                                      {getStatusText()}
                                    </Badge>

                                    {/* Delete Workout Button - Only remaining action */}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDeleteDialog(workout);
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </React.Fragment>
                        );
                      });
                      })()}
                    </div>

                    {/* Pagination for past workouts */}
                    {totalPages > 1 && pastWorkouts.length > 0 && (
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="text-sm text-muted-foreground">
                          {t('clientProfile.paginationShown', {
                            from: pastWorkouts.length > 0 ? startIndex + 1 : 0,
                            to: Math.min(endIndex, pastWorkouts.length),
                            total: pastWorkouts.length,
                          })}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={validCurrentPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            {t('clientProfile.back')}
                          </Button>
                          <span className="text-sm">
                            {t('clientProfile.pageOf', { page: validCurrentPage, pages: totalPages })}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={validCurrentPage === totalPages}
                          >
                            {t('clientProfile.next')}
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            </div>
          )}

          {activeSection === 'exercises' && (
            <div className="mt-6">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-lg font-semibold">{t('clientProfile.exercisesHeading')}</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto shrink-0 touch-manipulation"
                  onClick={() => {
                    setAddManualExerciseSearch('');
                    setAddManualExerciseSelectedName('');
                    setAddManualExerciseWeight('');
                    setShowAddManualExerciseDialog(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('clientProfile.addExerciseBtn')}
                </Button>
              </div>
            {loading || exerciseWeightsLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-2">{t('clientProfile.loading')}</span>
              </div>
            ) : displayExercises.length === 0 ? (
              <div className="text-center py-8">
                <Eye className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{t('clientProfile.noExercisesEmpty')}</p>
                <Button
                  type="button"
                  variant="secondary"
                  className="mt-4"
                  onClick={() => {
                    setAddManualExerciseSearch('');
                    setAddManualExerciseSelectedName('');
                    setAddManualExerciseWeight('');
                    setShowAddManualExerciseDialog(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('clientProfile.addExerciseWithWeight')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Search Input */}
                <div className="relative">
                  <Input
                    type="text"
                    placeholder={t('clientProfile.searchExercisePlaceholder')}
                    value={exerciseSearch}
                    onChange={(e) => handleExerciseSearch(e.target.value)}
                    className="w-full"
                  />
                </div>

                {/* Exercises List (grouped by muscle groups) */}
                {(() => {
                  const term = (exerciseSearch || '').trim().toLowerCase();
                  const filtered = (Array.isArray(displayExercises) ? displayExercises : []).filter((exercise) => {
                    if (!exercise?.name) return false;
                    return exerciseMatchesSearch(exercise.name, term, localizedAllExercises);
                  });

                  const findMuscleGroup = (exerciseName) => {
                    if (!exerciseName) return 'other';
                    const meta = (Array.isArray(allExercises) ? allExercises : []).find(
                      (ex) => ex?.value === exerciseName || ex?.label === exerciseName || ex?.name === exerciseName
                    );
                    return meta?.muscle_group || 'other';
                  };

                  const grouped = new Map();
                  for (const ex of filtered) {
                    const key = findMuscleGroup(ex.name);
                    if (!grouped.has(key)) grouped.set(key, []);
                    grouped.get(key).push(ex);
                  }

                  const orderedGroups = (Array.isArray(muscleGroupCatalog) ? muscleGroupCatalog : [])
                    .map((g) => ({
                      ...g,
                      items: (grouped.get(g.value) || []).slice().sort((a, b) =>
                        getExerciseLabel(a.name).localeCompare(getExerciseLabel(b.name), sortLocale)
                      ),
                    }))
                    .filter((g) => g.items.length > 0);

                  // Ensure "other" exists if there are unmapped exercises
                  const otherItems = (grouped.get('other') || []).slice().sort((a, b) =>
                    getExerciseLabel(a.name).localeCompare(getExerciseLabel(b.name), sortLocale)
                  );
                  if (otherItems.length > 0 && !orderedGroups.some((g) => g.value === 'other')) {
                    const otherMeta = (Array.isArray(muscleGroupCatalog) ? muscleGroupCatalog : []).find((g) => g.value === 'other') || {
                      value: 'other',
                      label: t('plan.muscle.other'),
                      color: 'bg-gray-100 text-gray-800 border-gray-300',
                    };
                    orderedGroups.push({ ...otherMeta, items: otherItems });
                  }

                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>
                          {term ? t('clientProfile.noExercisesFound') : t('clientProfile.noExercisesEmpty')}
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="max-h-96 overflow-y-auto rounded-md border">
                      {/* Sticky table header */}
                      <div className="sticky top-0 z-10 grid grid-cols-[1fr_auto_32px] items-center gap-2 bg-background px-4 py-2 text-xs font-semibold text-muted-foreground border-b">
                        <div>{t('clientProfile.colExercise')}</div>
                        <div className="text-right whitespace-nowrap">
                          {t('clientProfile.colWorkingWeightKg')}
                        </div>
                        <div />
                      </div>

                      <div className="divide-y">
                        {orderedGroups.map((group) => (
                          <div key={group.value}>
                            <div className="bg-muted/30 px-4 py-2 border-b">
                              <Badge variant="outline" className={`text-xs ${group.color || ''}`}>
                                {group.label}
                              </Badge>
                            </div>
                            <div className="divide-y">
                              {group.items.map((exercise) => {
                                const wwRow = workingWeightsByName?.[exercise.name];
                                const displayWeight = wwRow?.manual_working_weight ?? wwRow?.working_weight ?? null;
                                const weightText =
                                  typeof displayWeight === 'number' && isFinite(displayWeight)
                                    ? String(Math.round(Number(displayWeight)))
                                    : '—';

                                return (
                                  <div
                                    key={exercise.name}
                                    className="grid grid-cols-[1fr_auto_32px] items-center gap-2 px-4 py-3 hover:bg-accent/40 cursor-pointer"
                                    onClick={() => {
                                      setSelectedExerciseForAnalytics(exercise.name);
                                      setShowExerciseAnalytics(true);
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSelectedExerciseForAnalytics(exercise.name);
                                        setShowExerciseAnalytics(true);
                                      }
                                    }}
                                  >
                                    <div className="min-w-0">
                                      <div className="font-medium truncate">{getExerciseLabel(exercise.name)}</div>
                                    </div>
                                    <div className="text-right font-medium tabular-nums whitespace-nowrap">
                                      {weightText}
                                    </div>
                                    <div className="flex justify-end">
                                      <button
                                        type="button"
                                        className="p-1 rounded hover:bg-muted transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedExerciseForMaxWeight(exercise.name);
                                          const initial = wwRow?.manual_working_weight ?? wwRow?.working_weight ?? null;
                                          setNewMaxWeight(
                                            typeof initial === 'number' && isFinite(initial) ? String(initial) : ''
                                          );
                                          setShowUpdateMaxWeightDialog(true);
                                        }}
                                        title={t('clientProfile.editWorkingWeightTitle')}
                                        aria-label={t('clientProfile.editWorkingWeightTitle')}
                                      >
                                        <Pencil className="h-4 w-4 text-muted-foreground" />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            </div>
          )}

          {activeSection === 'payments' && (
            <div className="mt-6">
              <PaymentsTab
                clientId={client?.id}
                clientName={client?.nickname}
                trainerId={trainerId}
                openAddPaymentOnMount={initialOpenAddPayment}
              />
            </div>
          )}

          {/* Delete Profile Button - At the bottom */}
          <div className="mt-8 pt-6 pb-8 mb-4">
            <Button 
              variant="outline" 
              size="default"
              onClick={() => setShowDeleteClientDialog(true)}
              className="w-full sm:w-auto border-destructive text-destructive hover:bg-destructive/10 touch-manipulation"
              title={t('clientProfile.deleteProfileAria')}
              aria-label={t('clientProfile.deleteProfileAria')}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('clientProfile.deleteProfileBtn')}
            </Button>
          </div>

          </FullScreenModalBody>
        </FullScreenModalContent>
      </FullScreenModal>

      {/* Reschedule Panel */}
      <FullScreenModal open={showRescheduleDialog} onOpenChange={setShowRescheduleDialog}>
        <FullScreenModalContent className="flex flex-col">
          <FullScreenModalHeader>
            <FullScreenModalBackButton onClick={() => setShowRescheduleDialog(false)} />
            <FullScreenModalTitle>{t('workout.rescheduleTitle')}</FullScreenModalTitle>
            <Button
              variant="ghost"
              onClick={() => handleRescheduleWorkout(selectedWorkout?.id)}
              className="text-primary font-medium -mr-2 min-w-[90px]"
            >
              {t('workout.rescheduleSubmit')}
            </Button>
          </FullScreenModalHeader>

          <FullScreenModalBody>
            <div className="space-y-4 pb-6">
              <div className="text-sm text-muted-foreground">
                {t('workout.rescheduleHint')}
              </div>

              <div className="space-y-2">
                <Label htmlFor="reschedule-date">{t('clientProfile.rescheduleNewDate')}</Label>
                <Input
                  id="reschedule-date"
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  min={new Date().toLocaleDateString('en-CA')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reschedule-time">{t('clientProfile.rescheduleNewTime')}</Label>
                <Input
                  id="reschedule-time"
                  type="time"
                  value={rescheduleTime}
                  onChange={(e) => setRescheduleTime(e.target.value)}
                />
              </div>
            </div>
          </FullScreenModalBody>
        </FullScreenModalContent>
      </FullScreenModal>

      {/* Workout Detail Modal */}
      <WorkoutDetailModal
        show={showWorkoutDetail}
        onHide={() => setShowWorkoutDetail(false)}
        workout={selectedWorkout}
        trainerId={trainerId}
        onAddExercise={handleAddExerciseToWorkoutDetail}
        onAddNote={handleAddNote}
        onWorkoutUpdate={handleExerciseUpdateInWorkout}
        onExerciseUpdate={handleExerciseUpdateInWorkout}
      />

      <Dialog open={showPaymentCalendar} onOpenChange={setShowPaymentCalendar}>
        <DialogContent className="max-w-md">
          <VisuallyHidden asChild>
            <DialogTitle>{t('clientProfile.paymentCalendarTitle')}</DialogTitle>
          </VisuallyHidden>
          <VisuallyHidden asChild>
            <DialogDescription>{t('clientProfile.paymentCalendarDesc')}</DialogDescription>
          </VisuallyHidden>
          {paymentCalendarLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  aria-label={t('clientProfile.paymentCalPrevMonth')}
                  onClick={() =>
                    setPaymentCalendarMonth((prev) => {
                      const y = prev.getFullYear();
                      const m = prev.getMonth() - 1;
                      return new Date(y, m, 1);
                    })
                  }
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <span className="min-w-0 truncate text-center text-sm font-semibold capitalize">
                  {format(paymentCalendarMonth, 'LLLL yyyy', { locale: dfLocale })}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  aria-label={t('clientProfile.paymentCalNextMonth')}
                  onClick={() =>
                    setPaymentCalendarMonth((prev) => {
                      const y = prev.getFullYear();
                      const m = prev.getMonth() + 1;
                      return new Date(y, m, 1);
                    })
                  }
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
              <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] text-muted-foreground">
                {paymentCalWeekdays.map((wd) => (
                  <div key={wd}>{wd}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {paymentCalendarCells.map((cell, idx) => {
                  if (cell.type === 'empty') {
                    return <div key={`pc-empty-${idx}`} className="aspect-square min-h-[2.25rem]" />;
                  }
                  const { dayKey, day } = cell;
                  const billable = workouts.filter((w) => {
                    if (workoutCalendarDayKey(w) !== dayKey) return false;
                    if (w.status === 'cancelled' || w.status === 'transferred') return false;
                    return true;
                  });
                  let tone = null;
                  if (billable.length > 0) {
                    const anyUnpaid = billable.some((w) => paymentCalendarUnpaidIds.has(w.id));
                    tone = anyUnpaid ? 'unpaid' : 'paid';
                  }
                  const hasUpcomingBillable =
                    billable.length > 0 && billable.some((w) => !isWorkoutDebited(w));
                  const payQty = paymentQuantityByDayKey.get(dayKey) || 0;
                  return (
                    <div
                      key={dayKey}
                      className={cn(
                        'relative flex aspect-square min-h-[2.25rem] items-center justify-center rounded-md border p-0.5',
                        tone === 'paid' &&
                          !hasUpcomingBillable &&
                          'border-green-700 bg-green-600 text-white',
                        tone === 'paid' &&
                          hasUpcomingBillable &&
                          'border-green-700/55 bg-green-600/50 text-white',
                        tone === 'unpaid' &&
                          !hasUpcomingBillable &&
                          'border-red-700 bg-red-600 text-white',
                        tone === 'unpaid' &&
                          hasUpcomingBillable &&
                          'border-red-700/55 bg-red-600/50 text-white',
                        tone === null && 'border-border bg-muted/50 text-foreground'
                      )}
                    >
                      <span
                        className={cn(
                          'absolute left-0.5 top-0.5 text-[9px] font-medium leading-none',
                          (tone === 'paid' || tone === 'unpaid') && 'text-white/95',
                          tone === null && 'text-foreground/90'
                        )}
                      >
                        {day}
                      </span>
                      {payQty > 0 ? (
                        <span className="font-bold tabular-nums text-sm leading-none">{payQty}</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex flex-col gap-3 text-xs sm:text-sm">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                  <div className="flex h-8 items-center gap-2">
                    <span
                      className="h-8 w-8 shrink-0 rounded-md border border-green-700 bg-green-600"
                      aria-hidden
                    />
                    <span>{t('clientProfile.legendPaid')}</span>
                  </div>
                  <div className="flex h-8 items-center gap-2">
                    <span
                      className="h-8 w-8 shrink-0 rounded-md border border-red-700 bg-red-600"
                      aria-hidden
                    />
                    <span>{t('clientProfile.legendUnpaid')}</span>
                  </div>
                </div>
                <div className="flex h-8 items-center gap-2">
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-muted/50 text-[10px] font-bold tabular-nums text-foreground"
                    aria-hidden
                  >
                    3
                  </span>
                  <span className="text-black leading-none">
                    {t('clientProfile.legendPaidCount')}
                  </span>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Exercise Detail Dialog - REMOVED, now using collapsible list in exercises tab */}

      {/* Add Workout Panel */}
      <FullScreenModal open={showAddWorkout} onOpenChange={setShowAddWorkout}>
        <FullScreenModalContent className="flex flex-col">
          <FullScreenModalHeader>
            <FullScreenModalBackButton onClick={() => setShowAddWorkout(false)} />
            <FullScreenModalTitle>{t('clientProfile.addWorkoutTitle')}</FullScreenModalTitle>
            <Button
              variant="ghost"
              onClick={handleAddWorkout}
              className="text-primary font-medium -mr-2 min-w-[90px]"
            >
              {t('addWorkout.create')}
            </Button>
          </FullScreenModalHeader>

          <FullScreenModalBody>
          <div className="space-y-6 pb-6">
            <div className="text-sm text-muted-foreground">
              {t('clientProfile.addWorkoutIntro', { name: client?.nickname ?? '' })}
            </div>
            {errorMessage && (
              <div className="p-4 border border-destructive/50 rounded-md bg-destructive/10 text-destructive text-sm">
                {errorMessage}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2 min-w-0">
                <Label>{t('addWorkout.dateLabel')}</Label>
                <Input
                  type="date"
                  value={addWorkoutForm.date}
                  onChange={(e) => setAddWorkoutForm({...addWorkoutForm, date: e.target.value})}
                  min={new Date().toLocaleDateString('en-CA')}
                  className="min-w-0"
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="workout-time">{t('addWorkout.startTime')} *</Label>
                <Input
                  id="workout-time"
                  type="time"
                  value={addWorkoutForm.time}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  required
                  className="min-w-0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="workout-end-time">{t('addWorkout.endTime')}</Label>
              <Input
                id="workout-end-time"
                type="time"
                value={addWorkoutForm.end_time}
                onChange={(e) => setAddWorkoutForm({...addWorkoutForm, end_time: e.target.value})}
                placeholder={t('addWorkout.endTimePlaceholder')}
              />
              <p className="text-xs text-muted-foreground">{t('addWorkout.endTimeHint')}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="workout-comment">{t('addWorkout.commentLabel')}</Label>
              <Textarea
                id="workout-comment"
                rows={2}
                value={addWorkoutForm.comment}
                onChange={(e) => setAddWorkoutForm({...addWorkoutForm, comment: e.target.value})}
                placeholder={t('addWorkout.commentPlaceholder')}
                className="placeholder:text-slate-400"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recurrence">{t('addWorkout.recurrenceLabel')}</Label>
              <Select
                value={addWorkoutForm.recurrence_type}
                onValueChange={(value) => setAddWorkoutForm({...addWorkoutForm, recurrence_type: value})}
              >
                <SelectTrigger className="cursor-pointer">
                  <SelectValue placeholder={t('addWorkout.recurrencePlaceholder')} />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="none">{t('addWorkout.recurrenceNone')}</SelectItem>
                  <SelectItem value="weekly">{t('addWorkout.recurrenceWeekly')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {addWorkoutForm.recurrence_type === 'weekly' && (
              <div className="space-y-3">
                <Label>{t('addWorkout.weekdaysLabel')}</Label>
                <div className="flex flex-wrap gap-2">
                  {daysOfWeek.map(day => (
                    <Button
                      key={day.value}
                      type="button"
                      variant={addWorkoutForm.recurrence_days.includes(day.value) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleDayToggle(day.value)}
                      className="min-w-[40px]"
                    >
                      {day.label}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">{t('addWorkout.weekdaysHint')}</p>
              </div>
            )}
          </div>
          </FullScreenModalBody>
        </FullScreenModalContent>
      </FullScreenModal>

      {/* Add Exercise Panel */}
      <FullScreenModal open={showAddExercise} onOpenChange={setShowAddExercise}>
        <FullScreenModalContent className="flex flex-col">
          <FullScreenModalHeader>
            <FullScreenModalBackButton onClick={() => setShowAddExercise(false)} />
            <FullScreenModalTitle>{t('clientProfile.addExerciseBtn')}</FullScreenModalTitle>
            <Button
              variant="ghost"
              onClick={handleAddExercise}
              className="text-primary font-medium -mr-2 min-w-[90px]"
            >
              {t('common.add')}
            </Button>
          </FullScreenModalHeader>

          <FullScreenModalBody>
          <div className="space-y-4 pb-6">
            <div className="text-sm text-muted-foreground">
              {t('clientProfile.addExerciseToWorkout', {
                date:
                  selectedWorkoutForExercise && selectedWorkoutForExercise.date
                    ? formatDate(selectedWorkoutForExercise.date)
                    : t('clientProfile.chosenDateFallback'),
              })}
            </div>
            {errorMessage && (
              <div className="p-4 border border-destructive/50 rounded-md bg-destructive/10 text-destructive text-sm">
                {errorMessage}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="exercise-name" className="text-sm font-medium">
                {t('workout.exerciseNameLabel')} <span className="text-destructive">*</span>
              </Label>
              <Combobox
                value={addExerciseForm.name}
                onValueChange={(value) => {
                  setAddExerciseForm({ ...addExerciseForm, name: value });
                }}
                options={localizedAllExercises}
                placeholder={t('workout.alert.pickExerciseName')}
                emptyText={t('clientProfile.comboboxEmpty')}
                onCreateNew={promptSaveNewExercise}
                createNewText={t('clientProfile.comboboxCreate')}
                className="w-full"
              />
              <div className="text-xs text-muted-foreground space-y-1">
                <p>{t('clientProfile.comboboxHint')}</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <Dumbbell className="h-3 w-3 text-blue-500" />
                    <span>{t('clientProfile.standardExercises')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Dumbbell className="h-3 w-3 text-green-500" />
                    <span>{t('clientProfile.myDictionary')}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Label className="text-base font-medium">{t('workout.setsLabel')}</Label>
                  <span className="text-sm text-muted-foreground">
                    ({addExerciseForm.sets.length})
                  </span>
                </div>
                {addExerciseForm.sets.length < 10 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddSet}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('clientProfile.addSetLong')}</span>
                    <span className="sm:hidden">{t('clientProfile.addSetShort')}</span>
                  </Button>
                )}
              </div>

              <div className="space-y-3">
                {addExerciseForm.sets.map((set, index) => (
                  <Card key={index} className="border border-gray-200 hover:border-gray-300 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        {/* Set number badge */}
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                            {index + 1}
                          </div>
                        </div>

                        {/* Reps input */}
                        <div className="flex-1 min-w-0">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              {t('plan.repsPlaceholder')}
                            </Label>
                            <Input
                              type="number"
                              placeholder="0"
                              value={set.reps}
                              onChange={(e) => handleSetChange(index, 'reps', e.target.value)}
                              required
                              className="text-center"
                              min="1"
                            />
                          </div>
                        </div>

                        {/* Weight input */}
                        <div className="flex-1 min-w-0">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              {t('workout.weightInputShort')} ({t('clientProfile.unitKg')})
                            </Label>
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                placeholder="0"
                                value={set.weight}
                                onChange={(e) => handleSetChange(index, 'weight', e.target.value)}
                                className="text-center"
                                min="0"
                                step="any"
                              />
                              <span className="text-sm text-muted-foreground">
                                {t('clientProfile.unitKg')}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Remove button */}
                        {addExerciseForm.sets.length > 1 && (
                          <div className="flex-shrink-0">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveSet(index)}
                              className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Button to add more sets at the bottom */}
              {addExerciseForm.sets.length > 1 && addExerciseForm.sets.length < 10 && (
                <div className="flex justify-center mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddSet}
                    className="w-full"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('clientProfile.addAnotherSet')}
                  </Button>
                </div>
              )}

              {/* Summary */}
              <div className="text-sm text-muted-foreground text-center">
                {t('clientProfile.volumeTotal', {
                  kg: addExerciseForm.sets.reduce((total, set) => {
                    const reps = parseInt(set.reps) || 0;
                    const weight = parseFloat(set.weight) || 0;
                    return total + reps * weight;
                  }, 0),
                  unit: t('clientProfile.unitKg'),
                })}
              </div>
            </div>

          </div>
          </FullScreenModalBody>
        </FullScreenModalContent>
      </FullScreenModal>

      {/* Edit Workout Panel */}
      <FullScreenModal open={showEditWorkout} onOpenChange={setShowEditWorkout}>
        <FullScreenModalContent className="flex flex-col">
          <FullScreenModalHeader>
            <FullScreenModalBackButton onClick={() => setShowEditWorkout(false)} />
            <FullScreenModalTitle>{t('clientProfile.editWorkoutTitle')}</FullScreenModalTitle>
            <Button
              variant="ghost"
              onClick={handleSaveEditedWorkout}
              className="text-primary font-medium -mr-2 min-w-[90px]"
            >
              {t('common.save')}
            </Button>
          </FullScreenModalHeader>

          <FullScreenModalBody>
          <div className="space-y-4 pb-6">
            <div className="text-sm text-muted-foreground">{t('clientProfile.editWorkoutHint')}</div>
            {errorMessage && (
              <div className="p-4 border border-destructive/50 rounded-md bg-destructive/10 text-destructive text-sm">
                {errorMessage}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="edit-date">{t('addWorkout.dateLabel')}</Label>
              <Input
                id="edit-date"
                type="date"
                value={editWorkoutForm.date}
                onChange={(e) => setEditWorkoutForm({...editWorkoutForm, date: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-time">{t('addWorkout.startTime')} *</Label>
                <Input
                  id="edit-time"
                  type="time"
                  value={editWorkoutForm.time}
                  onChange={(e) => setEditWorkoutForm({...editWorkoutForm, time: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-end-time">{t('addWorkout.endTime')}</Label>
                <Input
                  id="edit-end-time"
                  type="time"
                  value={editWorkoutForm.end_time}
                  onChange={(e) => setEditWorkoutForm({...editWorkoutForm, end_time: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-comment">{t('addWorkout.commentLabel')}</Label>
              <Textarea
                id="edit-comment"
                rows={2}
                value={editWorkoutForm.comment}
                onChange={(e) => setEditWorkoutForm({...editWorkoutForm, comment: e.target.value})}
                placeholder={t('addWorkout.commentPlaceholder')}
                className="placeholder:text-slate-400"
              />
            </div>
          </div>
          </FullScreenModalBody>
        </FullScreenModalContent>
      </FullScreenModal>

      {/* Exercise Confirmation Dialog */}
      <AlertDialog open={showExerciseConfirmDialog} onOpenChange={setShowExerciseConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Dumbbell className="h-5 w-5 text-primary" />
              {t('workout.exerciseConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('workout.exerciseConfirmDesc', { name: pendingExerciseName })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => (pendingExerciseAction ? cancelSaveExercise() : cancelSaveNewExercise())}
            >
              {t('clientProfile.exerciseConfirmNo')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => (pendingExerciseAction ? confirmSaveExercise() : confirmSaveNewExercise())}
            >
              {t('clientProfile.exerciseConfirmYes')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Client Confirmation Dialog */}
      <AlertDialog open={showDeleteClientDialog} onOpenChange={(open) => {
        setShowDeleteClientDialog(open);
        if (!open) setDeleteClientMode('deactivate');
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('clientProfile.deleteClientTitle')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                {(client?.name || '').trim() ? (
                  <p>
                    <strong>{t('clientProfile.deleteClientLabel')}</strong> {String(client.name).trim()}
                  </p>
                ) : null}
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="deleteClientMode"
                      value="deactivate"
                      checked={deleteClientMode === 'deactivate'}
                      onChange={() => setDeleteClientMode('deactivate')}
                      className="mt-1"
                    />
                    <div>
                      <strong>{t('clientProfile.deleteDeactivateTitle')}</strong>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {t('clientProfile.deleteDeactivateDesc')}
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="deleteClientMode"
                      value="full"
                      checked={deleteClientMode === 'full'}
                      onChange={() => setDeleteClientMode('full')}
                      className="mt-1"
                    />
                    <div>
                      <strong>{t('clientProfile.deleteFullTitle')}</strong>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {t('clientProfile.deleteFullDesc')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingClient}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteClient}
              disabled={isDeletingClient}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingClient
                ? t('clientProfile.deleting')
                : deleteClientMode === 'full'
                  ? t('clientProfile.deleteFullConfirm')
                  : t('clientProfile.deleteDeactivateConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Workout Confirmation Dialog */}
      <AlertDialog open={!!workoutToDelete} onOpenChange={() => setWorkoutToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('clientProfile.deleteWorkoutTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('clientProfile.deleteWorkoutConfirm', {
                date: workoutToDelete ? formatDate(workoutToDelete.date) : '',
                time: workoutToDelete ? formatTime(workoutToDelete.time) : '',
              })}
              {workoutToDelete && isPartOfSeries(workoutToDelete) && (
                <div className="mt-3 p-3 bg-muted rounded-md">
                  <p className="text-sm font-medium mb-2">{t('clientProfile.recurringWorkout')}</p>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="deleteType"
                        value="single"
                        checked={deleteType === 'single'}
                        onChange={(e) => setDeleteType(e.target.value)}
                      />
                      <span className="text-sm">{t('clientProfile.deleteThisOnly')}</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="deleteType"
                        value="series"
                        checked={deleteType === 'series'}
                        onChange={(e) => setDeleteType(e.target.value)}
                      />
                      <span className="text-sm">{t('clientProfile.deleteWholeSeries')}</span>
                    </label>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setWorkoutToDelete(null)}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => workoutToDelete && handleDeleteWorkout(workoutToDelete.id, deleteType)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteType === 'series'
                ? t('clientProfile.deleteSeries')
                : t('clientProfile.deleteWorkoutAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Update Working Weight Panel */}
      <FullScreenModal open={showUpdateMaxWeightDialog} onOpenChange={setShowUpdateMaxWeightDialog}>
        <FullScreenModalContent className="flex flex-col">
          <FullScreenModalHeader>
            <FullScreenModalBackButton
              onClick={() => {
                setShowUpdateMaxWeightDialog(false);
                setSelectedExerciseForMaxWeight(null);
                setNewMaxWeight('');
              }}
            />
            <FullScreenModalTitle>{t('workout.updateWorkingWeightTitle')}</FullScreenModalTitle>
            <Button
              variant="ghost"
              disabled={isSavingWorkingWeight}
              onClick={async () => {
                if (!client?.id || !selectedExerciseForMaxWeight) return;
                setIsSavingWorkingWeight(true);
                try {
                  const raw = newMaxWeight.trim();
                  const payloadVal = raw === '' ? null : raw;

                  // validate if provided (allow 0+)
                  if (payloadVal !== null) {
                    const parsed = parseFloat(String(payloadVal).replace(',', '.'));
                    if (isNaN(parsed) || !isFinite(parsed) || parsed < 0) {
                      setErrorMessage(t('clientProfile.errInvalidWeightValue'));
                      setTimeout(() => setErrorMessage(''), 3000);
                      return;
                    }
                  }

                  await updateClientExerciseWorkingWeight(client.id, selectedExerciseForMaxWeight, payloadVal);

                  await refreshClientExerciseWeights();

                  setSuccessMessage(t('clientProfile.successWorkingWeightUpdated'));
                  setTimeout(() => setSuccessMessage(''), 3000);
                  setShowUpdateMaxWeightDialog(false);
                  setSelectedExerciseForMaxWeight(null);
                  setNewMaxWeight('');
                } catch (e) {
                  console.error('[ClientProfileModal] Failed to update working weight:', e);
                  const msg =
                    e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    e?.message ||
                    t('workout.err.workingWeightUpdate');
                  setErrorMessage(msg);
                  setTimeout(() => setErrorMessage(''), 4000);
                } finally {
                  setIsSavingWorkingWeight(false);
                }
              }}
              className="text-primary font-medium -mr-2 min-w-[90px]"
            >
              {isSavingWorkingWeight ? t('common.saving') : t('common.save')}
            </Button>
          </FullScreenModalHeader>

          <FullScreenModalBody>
            <div className="space-y-4 pb-6">
              <div className="text-sm text-muted-foreground">
                {t('clientProfile.exerciseLinePrefix')} {getExerciseLabel(selectedExerciseForMaxWeight)}
              </div>
              <div className="space-y-2">
                <Label htmlFor="max-weight">{t('workout.workingWeightKgLabel')}</Label>
                <Input
                  id="max-weight"
                  type="number"
                  step="0.1"
                  min="0"
                  value={newMaxWeight}
                  onChange={(e) => setNewMaxWeight(e.target.value)}
                  placeholder={t('clientProfile.enterWeightPlaceholder')}
                />
              </div>
            </div>
          </FullScreenModalBody>
        </FullScreenModalContent>
      </FullScreenModal>

      {/* Добавить упражнение + рабочий вес (без тренировки в истории) */}
      <FullScreenModal
        open={showAddManualExerciseDialog}
        onOpenChange={(open) => {
          setShowAddManualExerciseDialog(open);
          if (!open) {
            setAddManualExerciseSearch('');
            setAddManualExerciseSelectedName('');
            setAddManualExerciseWeight('');
          } else {
            loadAllExercises();
          }
        }}
      >
        <FullScreenModalContent className="flex flex-col">
          <FullScreenModalHeader>
            <FullScreenModalBackButton onClick={() => setShowAddManualExerciseDialog(false)} />
            <FullScreenModalTitle>{t('clientProfile.addExerciseBtn')}</FullScreenModalTitle>
            <Button
              variant="ghost"
              disabled={
                isSavingManualExercise ||
                !addManualExerciseSelectedName?.trim() ||
                !addManualExerciseWeight?.trim()
              }
              className="text-primary font-medium -mr-2 min-w-[90px]"
              onClick={async () => {
                if (!client?.id) return;
                const name = addManualExerciseSelectedName.trim();
                if (!name) return;
                if (displayExercises.some((e) => e.name === name)) {
                  toast.error(t('clientProfile.exerciseInList'));
                  return;
                }
                const raw = addManualExerciseWeight.trim();
                if (raw === '') {
                  setErrorMessage(t('clientProfile.enterWorkingWeight'));
                  setTimeout(() => setErrorMessage(''), 3000);
                  return;
                }
                const parsed = parseFloat(String(raw).replace(',', '.'));
                if (isNaN(parsed) || !isFinite(parsed) || parsed < 0) {
                  setErrorMessage(t('clientProfile.errWorkingWeightInvalid'));
                  setTimeout(() => setErrorMessage(''), 3000);
                  return;
                }
                const payloadVal = parsed;
                setIsSavingManualExercise(true);
                try {
                  await updateClientExerciseWorkingWeight(client.id, name, payloadVal);
                  await refreshClientExerciseWeights();
                  setSuccessMessage(t('clientProfile.manualExerciseAdded'));
                  setTimeout(() => setSuccessMessage(''), 3000);
                  setShowAddManualExerciseDialog(false);
                  setAddManualExerciseSearch('');
                  setAddManualExerciseSelectedName('');
                  setAddManualExerciseWeight('');
                  if (onExerciseUpdate) onExerciseUpdate();
                } catch (e) {
                  console.error('[ClientProfileModal] Add manual exercise failed:', e);
                  const msg =
                    e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    e?.message ||
                    t('clientProfile.errSaveFailed');
                  setErrorMessage(msg);
                  setTimeout(() => setErrorMessage(''), 4000);
                } finally {
                  setIsSavingManualExercise(false);
                }
              }}
            >
              {isSavingManualExercise ? t('common.saving') : t('common.save')}
            </Button>
          </FullScreenModalHeader>

          <FullScreenModalBody>
            <div className="space-y-4 pb-6">
              <p className="text-sm text-muted-foreground">{t('clientProfile.manualExerciseIntro')}</p>
              <div className="space-y-2">
                <Label htmlFor="add-ex-search">{t('clientProfile.searchLabel')}</Label>
                <Input
                  id="add-ex-search"
                  value={addManualExerciseSearch}
                  onChange={(e) => setAddManualExerciseSearch(e.target.value)}
                  placeholder={t('clientProfile.searchExerciseStart')}
                  autoComplete="off"
                />
              </div>
              {(() => {
                const term = addManualExerciseSearch.trim().toLowerCase();
                const taken = new Set(displayExercises.map((e) => e.name));
                const options = (Array.isArray(localizedAllExercises) ? localizedAllExercises : [])
                  .map((ex) => ({
                    ex,
                    name: getCatalogExerciseName(ex),
                    label: getCatalogExerciseLabel(ex),
                  }))
                  .filter((o) => o.name && !taken.has(o.name))
                  .filter((o) => {
                    if (!term) return true;
                    return (
                      o.name.toLowerCase().includes(term) || o.label.toLowerCase().includes(term)
                    );
                  })
                  .slice(0, 120);

                return (
                  <div className="space-y-2">
                    <Label>{t('workout.exerciseNameLabel')}</Label>
                    <div className="max-h-52 overflow-y-auto rounded-md border border-border bg-muted/20">
                      {options.length === 0 ? (
                        <p className="p-4 text-sm text-muted-foreground text-center">
                          {t('clientProfile.manualListEmpty')}
                        </p>
                      ) : (
                        options.map(({ ex, name, label }) => (
                          <button
                            key={`${name}-${ex?.id || ''}`}
                            type="button"
                            className={`w-full text-left px-3 py-2.5 text-sm border-b border-border last:border-b-0 transition-colors ${
                              addManualExerciseSelectedName === name
                                ? 'bg-primary/15 font-medium'
                                : 'hover:bg-accent/60 active:bg-accent'
                            }`}
                            onClick={() => setAddManualExerciseSelectedName(name)}
                          >
                            <span className="block truncate">{label || name}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                );
              })()}
              {addManualExerciseSelectedName ? (
                <p className="text-xs text-muted-foreground">
                  {t('clientProfile.selectedLabel')}{' '}
                  <span className="font-medium text-foreground">{getExerciseLabel(addManualExerciseSelectedName)}</span>
                </p>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="add-ex-weight" className="inline-flex items-baseline gap-0.5">
                  {t('clientProfile.workingWeightKgLabel')}
                  <span className="text-destructive leading-none" aria-hidden>
                    *
                  </span>
                </Label>
                <Input
                  id="add-ex-weight"
                  type="number"
                  step="0.5"
                  min="0"
                  required
                  value={addManualExerciseWeight}
                  onChange={(e) => setAddManualExerciseWeight(e.target.value)}
                  placeholder={t('clientProfile.weightExample')}
                />
              </div>
            </div>
          </FullScreenModalBody>
        </FullScreenModalContent>
      </FullScreenModal>

      {/* Exercise Analytics Modal */}
      <ExerciseAnalyticsModal
        show={showExerciseAnalytics}
        onHide={() => {
          setShowExerciseAnalytics(false);
          setSelectedExerciseForAnalytics(null);
        }}
        clientId={client?.id}
        exerciseName={selectedExerciseForAnalytics}
        exerciseDisplayName={
          selectedExerciseForAnalytics ? getExerciseLabel(selectedExerciseForAnalytics) : undefined
        }
        maxWeightHistory={exerciseMaxWeights[selectedExerciseForAnalytics]}
        onOpenWorkout={(session) => {
          const workoutId = session?.workout_id;
          if (!workoutId) return;

          // Build a minimal workout object for WorkoutDetailModal.
          // WorkoutDetailModal will fetch exercises/notes by workout.id.
          const workoutStub = {
            id: workoutId,
            client_id: client?.id,
            // Prefer YYYY-MM-DD string to avoid timezone parsing issues inside WorkoutDetailModal
            date: session?.dateStr || null,
            time: session?.timeStr || session?.time || null,
            end_time: null,
            comment: session?.workout_comment || '',
          };

          setSelectedWorkout(workoutStub);
          setShowWorkoutDetail(true);
        }}
      />

      <AvatarPickerModal
        open={showAvatarPicker}
        onOpenChange={setShowAvatarPicker}
        title={t('clientProfile.pickAvatar')}
        selectedUrl={client?.avatar}
        onSelect={handleSelectAvatarFromLibrary}
      />

    </>
  );
};

export default ClientProfileModal;
